/* OrgMembers — Enterprise member management for an organization.
   Driven exclusively by OrganizationRuntimeContext (seat limits + permissions)
   and live API calls (members list, member detail). */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, ConfirmDialog, SearchInput } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TabBar } from '../platform/_ui';
import {
  useOrgRuntime,
  useOrganization,
  useCurrentOrgUser,
  useLimits,
  useUsage,
} from './OrganizationRuntimeContext';
import { usePermissions } from './auth/usePermissions.js';
import { ORG } from './auth/capabilities.js';
import * as membersApi   from '../api/members.api.js';
import * as orgApi       from '../api/organization.api.js';
import * as auditApi     from '../api/audit.api.js';
import * as workspaceApi   from '../api/workspace.api.js';
import { orgRolesApi }     from '../api/org-roles.api.js';
import * as orgSecurityApi from '../api/org-security.api.js';
import { CAP_LABELS, CAP_GROUP_LABELS, groupCapabilities } from './auth/capabilities-meta.js';

/* ── Constants ──────────────────────────────────────────────────────────── */

const PAGE_SIZE = 25;

const ROLE_TONE  = { ORG_ADMIN: 'purple', MEMBER: 'slate', VIEWER: 'gray' };
const ROLE_LABEL = { ORG_ADMIN: 'Org Admin', MEMBER: 'Member', VIEWER: 'Viewer' };

const STATUS_TONE  = { ACTIVE: 'green', PENDING: 'amber', SUSPENDED: 'red', EXPIRED: 'gray' };
const STATUS_LABEL = { ACTIVE: 'Active', PENDING: 'Pending', SUSPENDED: 'Suspended', EXPIRED: 'Expired' };


const ROLE_OPTS = [
  { value: 'ORG_ADMIN', label: 'Org Admin' },
  { value: 'MEMBER',    label: 'Member'    },
  { value: 'VIEWER',    label: 'Viewer'    },
];

const ROLE_FILTER_OPTS = [
  { value: '',          label: 'All roles'  },
  { value: 'ORG_ADMIN', label: 'Org Admin'  },
  { value: 'MEMBER',    label: 'Member'     },
  { value: 'VIEWER',    label: 'Viewer'     },
];

const WS_ROLE_LABEL   = { WORKSPACE_ADMIN: 'WS Admin', WORKSPACE_USER: 'WS User' };
const WS_STATUS_LABEL = { ACTIVE: 'Active', ARCHIVED: 'Archived', SUSPENDED: 'Suspended' };

const STATUS_FILTER_OPTS = [
  { value: '',          label: 'All statuses'    },
  { value: 'ACTIVE',    label: 'Active'          },
  { value: 'PENDING',   label: 'Pending invite'  },
  { value: 'SUSPENDED', label: 'Suspended'       },
  { value: 'EXPIRED',   label: 'Expired'         },
];

const DRAWER_TABS = [
  { id: 'overview',            label: 'Overview',    icon: 'User'          },
  { id: 'profile',             label: 'Profile',     icon: 'Edit'          },
  { id: 'sessions',            label: 'Sessions',    icon: 'Monitor'       },
  { id: 'audit',               label: 'Audit',       icon: 'ClipboardList' },
  { id: 'workspace-summary',   label: 'Workspaces',  icon: 'LayoutGrid'    },
  { id: 'role-summary',        label: 'Role',        icon: 'Shield'        },
  { id: 'permission-summary',  label: 'Permissions', icon: 'Lock'          },
];

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const initials = (name) =>
  (name || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

const roleTone  = (r) => ROLE_TONE[r]   ?? 'gray';
const roleFmt   = (r) => ROLE_LABEL[r]  ?? r ?? 'Member';
const statTone  = (s) => STATUS_TONE[s] ?? 'gray';
const statFmt   = (s) => STATUS_LABEL[s] ?? (s ? s.charAt(0) + s.slice(1).toLowerCase() : 'Active');

const seatPct = (used, max) => (max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0);

function parseUA(ua) {
  if (!ua) return 'Unknown device';
  let browser = 'Browser';
  if (/Edg\//.test(ua))                              { const v = ua.match(/Edg\/([\d]+)/)?.[1];     browser = `Edge${v ? ' ' + v : ''}`; }
  else if (/Chrome\//.test(ua))                      { const v = ua.match(/Chrome\/([\d]+)/)?.[1];  browser = `Chrome${v ? ' ' + v : ''}`; }
  else if (/Firefox\//.test(ua))                     { const v = ua.match(/Firefox\/([\d]+)/)?.[1]; browser = `Firefox${v ? ' ' + v : ''}`; }
  else if (/Version\//.test(ua) && /Safari/.test(ua)){ const v = ua.match(/Version\/([\d]+)/)?.[1]; browser = `Safari${v ? ' ' + v : ''}`; }
  let os = '';
  if (/iPhone|iPad/.test(ua))       os = 'iOS';
  else if (/Android/.test(ua))      os = 'Android';
  else if (/Windows NT/.test(ua))   os = 'Windows';
  else if (/Mac OS X/.test(ua))     os = 'macOS';
  else if (/Linux/.test(ua))        os = 'Linux';
  return os ? `${browser} on ${os}` : browser;
}
function deviceIcon(ua) {
  if (!ua) return 'Monitor';
  if (/iPhone|(?:Android.*Mobile)|Windows Phone/i.test(ua)) return 'Smartphone';
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua))          return 'Tablet';
  return 'Monitor';
}

function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000)           return 'just now';
  if (ms < 3_600_000)        return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000)       return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 7 * 86_400_000)   return `${Math.floor(ms / 86_400_000)}d ago`;
  return fmtDate(iso);
}

const stepDotBg = (done, active) => { if (done) return 'var(--nx-success)'; if (active) return 'var(--nx-accent)'; return 'var(--nx-border)'; };
const seatBarColor = (pct) => {
  if (pct >= 90) return 'var(--nx-danger)';
  if (pct >= 70) return 'var(--nx-warning)';
  return 'var(--nx-accent)';
};

/* ── useMembers hook ─────────────────────────────────────────────────────── */

function useMembers(orgId) {
  const [all,       setAll]       = useState([]);
  const [allTotal,  setAllTotal]  = useState(0);
  const [members,   setMembers]   = useState([]);
  const [pag,       setPag]       = useState({ page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [q,         setQ]         = useState('');
  const [statusF,   setStatusF]   = useState('');
  const [roleF,     setRoleF]     = useState('');
  // LOW-07: sort state — refs keep current values stable in loadPage closure
  const [sortField, setSortField] = useState('joinedAt');
  const [sortDir,   setSortDir]   = useState('desc');
  const sortFieldRef              = useRef('joinedAt');
  const sortDirRef                = useRef('desc');
  const debRef                    = useRef(null);

  const loadFull = useCallback(async () => {
    try {
      const r = await membersApi.list(orgId, { pageSize: 200 });
      setAll(r.data ?? []);
      setAllTotal(r.pagination?.total ?? 0);
    } catch { /* summary counts are optional */ }
  }, [orgId]);

  const loadPage = useCallback(async (search, status, role, page) => {
    setLoading(true);
    try {
      const r = await membersApi.list(orgId, {
        search:   search || undefined,
        status:   status || undefined,
        role:     role   || undefined,
        page,
        pageSize: PAGE_SIZE,
        sort:     sortFieldRef.current,     // LOW-07
        sortDir:  sortDirRef.current,       // LOW-07
      });
      setMembers(r.data ?? []);
      setPag(r.pagination ?? { page, pageSize: PAGE_SIZE, total: 0, totalPages: 1 });
      setError(null);
    } catch (err) {
      setError(err?.message ?? 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadFull();
    loadPage('', '', '', 1);
  }, [loadFull, loadPage]);

  const search = useCallback((v) => {
    setQ(v);
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => loadPage(v, statusF, roleF, 1), 350);
  }, [statusF, roleF, loadPage]);

  const filterStatus = useCallback((v) => {
    setStatusF(v);
    loadPage(q, v, roleF, 1);
  }, [q, roleF, loadPage]);

  const filterRole = useCallback((v) => {
    setRoleF(v);
    loadPage(q, statusF, v, 1);
  }, [q, statusF, loadPage]);

  // LOW-12: single loadPage call to avoid 3 intermediate API calls
  const clearFilters = useCallback(() => {
    clearTimeout(debRef.current);
    setQ('');
    setStatusF('');
    setRoleF('');
    loadPage('', '', '', 1);
  }, [loadPage]);

  const goPage = useCallback((p) => loadPage(q, statusF, roleF, p), [q, statusF, roleF, loadPage]);

  // LOW-07: toggle sort — same field toggles direction; new field defaults to asc
  const sortBy = useCallback((field) => {
    const nextDir = sortFieldRef.current === field && sortDirRef.current === 'asc' ? 'desc' : 'asc';
    sortFieldRef.current = field;
    sortDirRef.current   = nextDir;
    setSortField(field);
    setSortDir(nextDir);
    loadPage(q, statusF, roleF, 1);
  }, [q, statusF, roleF, loadPage]);

  const refresh = useCallback(() => {
    loadFull();
    loadPage(q, statusF, roleF, pag.page);
  }, [q, statusF, roleF, pag.page, loadFull, loadPage]);

  const stats = useMemo(() => ({
    total:     allTotal,
    active:    all.filter((m) => m.status === 'ACTIVE').length,
    pending:   all.filter((m) => m.status === 'PENDING').length,
    suspended: all.filter((m) => m.status === 'SUSPENDED').length,
    expired:   all.filter((m) => m.status === 'EXPIRED').length,
  }), [all, allTotal]);

  return {
    members, pag, loading, error,
    q, statusF, roleF, stats,
    sortField, sortDir,
    search, filterStatus, filterRole, clearFilters, goPage, sortBy, refresh,
  };
}

/* ── SeatBanner ──────────────────────────────────────────────────────────── */

function SeatBanner({ used, max }) {
  const pct   = seatPct(used, max);
  const full  = used >= max;
  const label = full
    ? `Seat limit reached (${used}/${max}). No new members can be invited until seats are freed or your plan is upgraded.`
    : `Seat utilization at ${pct}% (${used} of ${max}). Consider upgrading before the limit is reached.`;
  const tone  = full ? 'var(--nx-danger)' : 'var(--nx-warning)';
  return (
    <div role="alert" aria-live="polite" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 14, borderRadius: 8, background: full ? 'rgba(220,38,38,0.08)' : 'rgba(245,158,11,0.08)', border: `1px solid ${tone}`, color: tone, fontSize: 13 }}>
      <NexusIcon name={full ? 'AlertOctagon' : 'AlertTriangle'} size={15} />
      <span>{label}</span>
    </div>
  );
}

/* ── SeatSummaryStrip ────────────────────────────────────────────────────── */

function SeatSummaryStrip({ stats, limits, usageUsers }) {
  const maxSeats  = limits.maxUsers ?? 0;
  const usedSeats = usageUsers ?? stats.active;
  const available = maxSeats > 0 ? maxSeats - usedSeats : null;
  const pct       = seatPct(usedSeats, maxSeats);
  const barColor  = seatBarColor(pct);

  return (
    <div className="nx-cc-stat-grid nx-kpi-5">
      <StatCard label="Total Members" value={stats.total}     icon="Users"       color="blue"   />
      <StatCard label="Active"        value={stats.active}    icon="CheckCircle" color="green"  />
      <StatCard label="Pending"       value={stats.pending}   icon="Clock"       color="amber"  />
      <StatCard label="Suspended"     value={stats.suspended} icon="PauseCircle" color="red"    />
      <StatCard label="Expired"       value={stats.expired}   icon="Archive"     color="gray"   />
      {maxSeats > 0 && (
        <>
          <StatCard label="Seats Used" value={`${usedSeats} / ${maxSeats}`} icon="Users" color={pct >= 90 ? 'red' : pct >= 70 ? 'amber' : 'blue'} />
          <div className="nx-stat-card">
            <div className="nx-stat-label">Seat Utilization</div>
            <div className="nx-stat-value" style={{ fontSize: 15 }}>{pct}%</div>
            <div style={{ height: 4, borderRadius: 2, background: 'var(--nx-border)', marginTop: 6, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 2, transition: 'width 0.4s' }} />
            </div>
            {available !== null && <div className="nx-stat-sub">{available} available</div>}
          </div>
        </>
      )}
    </div>
  );
}

/* ── MembersTable ────────────────────────────────────────────────────────── */

function MemberRow({ m, selected, onSelect, onOpen, onAction, isSelf, orgId }) {
  const navigate  = useNavigate();
  const rowPerm   = usePermissions();
  const canManage = rowPerm.flags.canManageMembers || rowPerm.isSA;
  const checked   = selected.has(m.id);
  return (
    <tr className="nx-tr" style={{ cursor: 'pointer' }}>
      <td onClick={(e) => e.stopPropagation()}>
        {canManage && !isSelf && (
          <input type="checkbox" checked={checked} onChange={() => onSelect(m.id)} style={{ cursor: 'pointer' }} aria-label={`Select ${m.name}`} />
        )}
      </td>
      <td onClick={() => onOpen(m)}>
        <span className="nx-row-name">
          <span className="nx-domain-mark" style={{ width: 26, height: 26, fontSize: 10, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', flexShrink: 0 }}>
            {initials(m.name)}
          </span>
          <span className="nx-row-name-text">
            {m.name && m.name !== m.email ? m.name : m.email.split('@')[0]}
          </span>
        </span>
      </td>
      <td onClick={() => onOpen(m)} style={{ maxWidth: 200 }}>
        <span className="nx-muted" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</span>
      </td>
      <td onClick={() => onOpen(m)}><Pill tone={roleTone(m.orgRole)}>{roleFmt(m.orgRole)}</Pill></td>
      <td onClick={(e) => { e.stopPropagation(); if (m._count?.memberships > 0) navigate(`/organization/${orgId}/iam?tab=member-assignments&memberId=${m.id}`); }}>
        <span className="nx-muted nx-mini" style={{ cursor: m._count?.memberships > 0 ? 'pointer' : 'default', textDecoration: m._count?.memberships > 0 ? 'underline' : 'none' }}>
          {m._count?.memberships > 0 ? `${m._count.memberships} ws` : '—'}
        </span>
      </td>
      <td onClick={() => onOpen(m)}><Pill tone={statTone(m.status)}>{statFmt(m.status)}</Pill></td>
      <td onClick={() => onOpen(m)}><span className="nx-muted nx-mini">{fmtDate(m.joinedAt ?? m.invitedAt)}</span></td>
      <td onClick={() => onOpen(m)}><span className="nx-muted nx-mini">{fmtDate(m.user?.lastLoginAt)}</span></td>
      <td className="nx-td-end" onClick={(e) => e.stopPropagation()}>
        {canManage && !isSelf && (
          <div style={{ display: 'flex', gap: 4 }}>
            {m.status === 'PENDING' && (
              <>
                <Btn variant="ghost" size="sm" icon="Mail" onClick={() => onAction('resend-invite', m)}>Resend</Btn>
                <Btn variant="ghost" size="sm" icon="X" onClick={() => onAction('cancel-invite', m)}>Cancel</Btn>
              </>
            )}
            {m.status === 'ACTIVE' && (
              <Btn variant="ghost" size="sm" icon="PauseCircle" onClick={() => onAction('suspend', m)}>Suspend</Btn>
            )}
            {m.status === 'SUSPENDED' && (
              <Btn variant="ghost" size="sm" icon="Play" onClick={() => onAction('activate', m)}>Activate</Btn>
            )}
            <Btn variant="ghost" size="sm" icon="ChevronRight" onClick={() => onOpen(m)} />
          </div>
        )}
        {(!canManage || isSelf) && (
          <Btn variant="ghost" size="sm" icon="ChevronRight" onClick={() => onOpen(m)} />
        )}
      </td>
    </tr>
  );
}

function TableSkeleton() {
  return (
    <tbody>
      {Array.from({ length: 6 }).map((_, i) => (
        <tr key={i} className="nx-tr">
          {Array.from({ length: 9 }).map((__, j) => (
            <td key={j}><div className="nx-skeleton" style={{ height: 14, width: j === 1 ? 120 : j === 2 ? 160 : 70 }} /></td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

function TableBody({ members, selected, onSelect, onOpen, onAction, currentUserId, orgId, statusF }) {
  if (members.length === 0) {
    let emptyMsg = 'No members match your filters.';
    if (statusF === 'PENDING')   emptyMsg = 'No pending invitations.';
    if (statusF === 'SUSPENDED') emptyMsg = 'No suspended members.';
    if (statusF === 'EXPIRED')   emptyMsg = 'No expired invitations.';
    if (statusF === 'ACTIVE')    emptyMsg = 'No active members.';
    return (
      <tbody>
        <tr>
          <td colSpan={9}>
            <div className="nx-empty" style={{ padding: '32px 0' }}>
              <NexusIcon name="Users" size={24} />
              <span>{emptyMsg}</span>
            </div>
          </td>
        </tr>
      </tbody>
    );
  }
  return (
    <tbody>
      {members.map((m) => (
        <MemberRow key={m.id} m={m} orgId={orgId} selected={selected} onSelect={onSelect} onOpen={onOpen} onAction={onAction} isSelf={m.userId === currentUserId} />
      ))}
    </tbody>
  );
}

// LOW-07: sort indicator for column headers
function SortIndicator({ field, sortField, sortDir }) {
  if (sortField !== field) return <span style={{ opacity: 0.25, fontSize: 10, marginLeft: 2 }}>↕</span>;
  return <span style={{ fontSize: 10, marginLeft: 2 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
}

// DEFECT-V01: SortTh defined at module scope to prevent unmount/remount on every MembersTable render
function SortTh({ field, children, style, onSort, sortField, sortDir }) {
  return (
    <th scope="col" style={{ cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => onSort?.(field)}>
      {children}<SortIndicator field={field} sortField={sortField} sortDir={sortDir} />
    </th>
  );
}

function MembersTable({ members, loading, error, pag, selected, onSelect, onSelectAll, onOpen, onAction, currentUserId, goPage, orgId, statusF, sortField, sortDir, onSort }) {
  const tablePerm = usePermissions();
  const canManage = tablePerm.flags.canManageMembers || tablePerm.isSA;
  const selectableMembers = members.filter((m) => m.userId !== currentUserId);
  const allChecked = selectableMembers.length > 0 && selectableMembers.every((m) => selected.has(m.id));

  if (error) {
    return (
      <div className="nx-empty">
        <NexusIcon name="AlertCircle" size={22} />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <>
      <div className="nx-table-wrap">
        <div className="nx-table-scroll">
          <table className="nx-table">
            <thead>
              <tr>
                <th scope="col" style={{ width: 36 }}>
                  {canManage && (
                    <input type="checkbox" checked={allChecked} onChange={() => onSelectAll(members)} style={{ cursor: 'pointer' }} aria-label="Select all members on this page" />
                  )}
                </th>
                <SortTh field="name"    onSort={onSort} sortField={sortField} sortDir={sortDir}>Name</SortTh>
                <SortTh field="email"   onSort={onSort} sortField={sortField} sortDir={sortDir} style={{ maxWidth: 200, width: 180 }}>Email</SortTh>
                <SortTh field="orgRole" onSort={onSort} sortField={sortField} sortDir={sortDir}>Org Role</SortTh>
                <th scope="col">Workspaces</th>
                <SortTh field="status"   onSort={onSort} sortField={sortField} sortDir={sortDir}>Status</SortTh>
                <SortTh field="joinedAt" onSort={onSort} sortField={sortField} sortDir={sortDir}>Joined</SortTh>
                <th scope="col">Last Login</th>
                <th scope="col" />
              </tr>
            </thead>
            {loading
              ? <TableSkeleton />
              : <TableBody members={members} selected={selected} onSelect={onSelect} onOpen={onOpen} onAction={onAction} currentUserId={currentUserId} orgId={orgId} statusF={statusF} />
            }
          </table>
        </div>
      </div>
      {pag.totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 4px', fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <span>{pag.total} total · page {pag.page} of {pag.totalPages}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn variant="ghost" size="sm" icon="ChevronLeft" disabled={pag.page <= 1} onClick={() => goPage(pag.page - 1)}>Prev</Btn>
            <Btn variant="ghost" size="sm" icon="ChevronRight" disabled={pag.page >= pag.totalPages} onClick={() => goPage(pag.page + 1)}>Next</Btn>
          </div>
        </div>
      )}
    </>
  );
}

/* ── BulkBar ─────────────────────────────────────────────────────────────── */

function BulkBar({ selected, members, onClear, onBulkAction, busy }) {
  const ids        = [...selected];
  const selRows    = members.filter((m) => selected.has(m.id));
  const anyActive  = selRows.some((m) => m.status === 'ACTIVE');
  const anyInactive = selRows.some((m) => m.status === 'SUSPENDED');
  const allPending = selRows.length > 0 && selRows.every((m) => m.status === 'PENDING');
  const anyPending = selRows.some((m) => m.status === 'PENDING');
  const anyNonPending = selRows.some((m) => m.status !== 'PENDING');

  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 80, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.14)', fontSize: 13 }}>
      <span style={{ fontWeight: 600 }}>{ids.length} selected</span>
      <div style={{ width: 1, height: 18, background: 'var(--nx-border)' }} />
      {anyPending    && <Btn variant="secondary" size="sm" icon="Mail"        disabled={busy} onClick={() => onBulkAction('resend-invite')}>Resend Invite</Btn>}
      {/* LOW-11: show Cancel Invite even on mixed selection, but disabled with tooltip explaining why */}
      {anyPending    && (
        <span title={anyNonPending ? 'Cancel Invite is only available when all selected members are pending' : undefined}>
          <Btn variant="ghost" size="sm" icon="X" disabled={busy || anyNonPending} onClick={() => onBulkAction('cancel-invite')}>Cancel Invite</Btn>
        </span>
      )}
      {anyInactive   && <Btn variant="secondary" size="sm" icon="Play"        disabled={busy} onClick={() => onBulkAction('activate')}>Activate</Btn>}
      {anyActive     && <Btn variant="secondary" size="sm" icon="PauseCircle" disabled={busy} onClick={() => onBulkAction('suspend')}>Suspend</Btn>}
      {anyNonPending && <Btn variant="danger"    size="sm" icon="Trash"       disabled={busy} onClick={() => onBulkAction('remove')}>Remove</Btn>}
      <Btn variant="ghost" size="sm" icon="X" onClick={onClear}>Clear</Btn>
    </div>
  );
}

/* ── MemberDrawer ────────────────────────────────────────────────────────── */

function WorkspaceSummaryTab({ orgId, memberId, memberStatus, wss, memberships, loading, canManage, onRefresh, onModalChange }) {
  const toast    = useToast();
  const navigate = useNavigate();

  /* ── Workspace-scoped custom roles (for edit dropdown) ─────────────────── */
  const [wsCustomRoles, setWsCustomRoles] = useState([]);
  useEffect(() => {
    let mounted = true;
    orgRolesApi.list(orgId)
      .then((r) => { if (mounted) setWsCustomRoles((r?.data ?? r ?? []).filter((role) => role.scope === 'workspace')); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [orgId]);

  /* ── Assign panel ──────────────────────────────────────────────────────── */
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignWsId, setAssignWsId] = useState('');
  const [assignRole, setAssignRole] = useState('WORKSPACE_USER');
  const [assignBusy, setAssignBusy] = useState(false);

  /* ── Inline edit ────────────────────────────────────────────────────────── */
  const [editingId,       setEditingId]       = useState(null);
  const [editRole,        setEditRole]        = useState('');
  const [editCustomRoleId, setEditCustomRoleId] = useState('');
  const [savingId,        setSavingId]        = useState(null);

  /* ── Remove confirm ─────────────────────────────────────────────────────── */
  const [confirmRemoveMs, setConfirmRemoveMs] = useState(null);
  const [removingId,      setRemovingId]      = useState(null);

  /* ── Archived section ───────────────────────────────────────────────────── */
  const [archiveOpen, setArchiveOpen] = useState(false);

  /* ── Derived data ───────────────────────────────────────────────────────── */
  const activeWss          = wss.filter((w) => w.status === 'ACTIVE');
  const activeMemberships  = memberships.filter((m) => m.workspace?.status === 'ACTIVE');
  const archivedMemberships = memberships.filter((m) => m.workspace && m.workspace.status !== 'ACTIVE');
  const sortedActive       = [...activeMemberships].sort((a, b) => (a.workspace?.name ?? '').localeCompare(b.workspace?.name ?? ''));
  const assignedWsIds      = new Set(memberships.map((m) => m.workspace?.id).filter(Boolean));
  const unassignedActiveWss = activeWss.filter((w) => !assignedWsIds.has(w.id));

  /* ── Helpers ────────────────────────────────────────────────────────────── */
  const openEdit = (ms) => { setEditingId(ms.id); setEditRole(ms.wsRole); setEditCustomRoleId(ms.wsCustomRole?.id ?? ''); };
  const cancelEdit = () => { setEditingId(null); setEditRole(''); setEditCustomRoleId(''); };

  const openConfirmRemove = (ms) => { setConfirmRemoveMs(ms); onModalChange?.(true); };
  const closeConfirmRemove = () => { setConfirmRemoveMs(null); onModalChange?.(false); };

  /* ── Actions ────────────────────────────────────────────────────────────── */
  const handleAssign = async () => {
    if (!assignWsId) return;
    setAssignBusy(true);
    try {
      await workspaceApi.addMember(assignWsId, { memberId, wsRole: assignRole });
      setAssignOpen(false);
      setAssignWsId('');
      setAssignRole('WORKSPACE_USER');
      onRefresh();
      toast({ tone: 'success', icon: 'LayoutGrid', title: 'Workspace assigned' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Assignment failed', desc: err?.message });
    } finally {
      setAssignBusy(false);
    }
  };

  const handleSaveEdit = async (ms) => {
    setSavingId(ms.id);
    try {
      const body = {};
      if (editRole !== ms.wsRole) body.wsRole = editRole;
      const prevCustId = ms.wsCustomRole?.id ?? '';
      if (editCustomRoleId !== prevCustId) body.wsCustomRoleId = editCustomRoleId || null;
      if (Object.keys(body).length === 0) { cancelEdit(); return; }
      await workspaceApi.updateMember(ms.workspace.id, memberId, body);
      cancelEdit();
      onRefresh();
      toast({ tone: 'success', icon: 'Check', title: 'Assignment updated' });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Update failed', desc: err?.message });
    } finally {
      setSavingId(null);
    }
  };

  const handleRemove = async () => {
    if (!confirmRemoveMs) return;
    const ms = confirmRemoveMs;
    setRemovingId(ms.id);
    try {
      await workspaceApi.removeMember(ms.workspace.id, memberId);
      closeConfirmRemove();
      onRefresh();
      toast({ tone: 'success', icon: 'Trash', title: 'Removed from workspace', desc: ms.workspace?.name });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Remove failed', desc: err?.message });
    } finally {
      setRemovingId(null);
    }
  };

  /* ── Loading skeleton ───────────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}>
      {[100, 85, 65].map((w, i) => (
        <div key={i} style={{ height: 56, borderRadius: 8, width: `${w}%`, background: 'var(--nx-surface-alt)', animation: 'nx-pulse 1.4s ease-in-out infinite' }} />
      ))}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* ── Suspended member warning ─────────────────────────────────── */}
      {memberStatus === 'SUSPENDED' && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, background: 'var(--nx-warning-tint)', border: '1px solid color-mix(in srgb, var(--nx-warning) 35%, transparent)', fontSize: 12 }}>
          <NexusIcon name="AlertTriangle" size={14} style={{ color: 'var(--nx-warning)', flexShrink: 0, marginTop: 1 }} />
          <span style={{ color: 'var(--nx-text)' }}>
            <strong>Member suspended.</strong> Workspace assignments are preserved but the member cannot authenticate.
          </span>
        </div>
      )}

      {/* ── Header: count + actions ──────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)' }}>{activeMemberships.length}</span>
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginLeft: 4 }}>
            of {activeWss.length} active workspace{activeWss.length !== 1 ? 's' : ''} assigned
          </span>
        </div>
        <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={onRefresh} title="Refresh workspace assignments" />
        {canManage && activeWss.length > 0 && (
          <Btn
            variant="secondary"
            size="sm"
            icon={assignOpen ? 'ChevronUp' : 'Plus'}
            disabled={unassignedActiveWss.length === 0}
            title={unassignedActiveWss.length === 0 ? 'All active workspaces are already assigned' : undefined}
            onClick={() => { if (unassignedActiveWss.length > 0) { setAssignOpen((v) => !v); setAssignWsId(''); setAssignRole('WORKSPACE_USER'); } }}
          >
            Assign Workspace
          </Btn>
        )}
      </div>

      {/* ── Assign panel ─────────────────────────────────────────────── */}
      {assignOpen && (
        <div style={{ borderRadius: 8, border: '1px solid var(--nx-border)', padding: '12px 14px', background: 'var(--nx-surface-alt)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text)' }}>Assign to Workspace</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <select
              className="nx-input"
              style={{ flex: 2, minWidth: 0, fontSize: 12 }}
              value={assignWsId}
              onChange={(e) => setAssignWsId(e.target.value)}
            >
              <option value="">Select workspace…</option>
              {unassignedActiveWss.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
            <select
              className="nx-input"
              style={{ flex: 1, minWidth: 110, fontSize: 12 }}
              value={assignRole}
              onChange={(e) => setAssignRole(e.target.value)}
            >
              <option value="WORKSPACE_USER">WS User</option>
              <option value="WORKSPACE_ADMIN">WS Admin</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" size="sm" onClick={() => setAssignOpen(false)} disabled={assignBusy}>Cancel</Btn>
            <Btn variant="primary" size="sm" icon="Check" onClick={handleAssign} disabled={assignBusy || !assignWsId}>
              {assignBusy ? 'Assigning…' : 'Assign'}
            </Btn>
          </div>
        </div>
      )}

      {/* ── Active assignments ────────────────────────────────────────── */}
      {sortedActive.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 0', textAlign: 'center' }}>
          <NexusIcon name="LayoutGrid" size={22} style={{ color: 'var(--nx-text-muted)' }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>No workspace assignments</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
              This member has org-level access only.
              {canManage && unassignedActiveWss.length > 0 && ' Use "Assign Workspace" above to add access.'}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sortedActive.map((ms) => {
            const isEditing  = editingId === ms.id;
            const isSaving   = savingId  === ms.id;
            const isRemoving = removingId === ms.id;
            return (
              <div key={ms.id} style={{ borderRadius: 8, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
                {/* ── Main row ── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
                  <div className="nx-domain-mark" style={{ width: 30, height: 30, fontSize: 12, flexShrink: 0, background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)' }}>
                    {(ms.workspace?.name ?? '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ms.workspace?.name ?? '—'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>
                      {ms.status === 'PENDING' ? 'Pre-assigned · ' : 'Assigned '}{fmtDate(ms.createdAt)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {ms.status === 'PENDING' && <Pill tone="amber" size="xs">Pre-assigned</Pill>}
                    <Pill tone={ms.wsRole === 'WORKSPACE_ADMIN' ? 'purple' : 'slate'}>{WS_ROLE_LABEL[ms.wsRole] ?? ms.wsRole}</Pill>
                    {ms.wsCustomRole && <Pill tone="amber">{ms.wsCustomRole.name}</Pill>}
                  </div>
                  {canManage && !isEditing && (
                    <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                      <button
                        type="button"
                        title="Edit assignment"
                        disabled={isRemoving}
                        onClick={() => openEdit(ms)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: '4px 6px', borderRadius: 4, display: 'flex', alignItems: 'center' }}
                      >
                        <NexusIcon name="Pencil" size={13} />
                      </button>
                      <button
                        type="button"
                        title="Remove from workspace"
                        disabled={isRemoving}
                        onClick={() => openConfirmRemove(ms)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-danger)', padding: '4px 6px', borderRadius: 4, display: 'flex', alignItems: 'center', opacity: isRemoving ? 0.5 : 1 }}
                      >
                        <NexusIcon name="Trash2" size={13} />
                      </button>
                    </div>
                  )}
                </div>
                {/* ── Edit panel ── */}
                {isEditing && (
                  <div style={{ padding: '10px 12px', background: 'color-mix(in srgb, var(--nx-accent) 4%, var(--nx-surface))', borderTop: '1px solid var(--nx-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 130 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Role</div>
                        <select className="nx-input" style={{ fontSize: 12 }} value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                          <option value="WORKSPACE_USER">WS User</option>
                          <option value="WORKSPACE_ADMIN">WS Admin</option>
                        </select>
                      </div>
                      <div style={{ flex: 2, minWidth: 160 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                          Custom Role <span style={{ fontWeight: 400 }}>(optional)</span>
                        </div>
                        <select className="nx-input" style={{ fontSize: 12 }} value={editCustomRoleId} onChange={(e) => setEditCustomRoleId(e.target.value)}>
                          <option value="">No custom role</option>
                          {wsCustomRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <Btn variant="ghost" size="sm" onClick={cancelEdit} disabled={isSaving}>Cancel</Btn>
                      <Btn variant="primary" size="sm" icon="Check" onClick={() => handleSaveEdit(ms)} disabled={isSaving}>
                        {isSaving ? 'Saving…' : 'Save Changes'}
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Archived workspace assignments ────────────────────────────── */}
      {archivedMemberships.length > 0 && (
        <div style={{ borderRadius: 8, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
          <button
            type="button"
            onClick={() => setArchiveOpen((v) => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--nx-surface-alt)', border: 'none', cursor: 'pointer', textAlign: 'left' }}
          >
            <NexusIcon name="Archive" size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--nx-text-muted)' }}>
              Archived workspaces ({archivedMemberships.length})
            </span>
            <NexusIcon name={archiveOpen ? 'ChevronUp' : 'ChevronDown'} size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
          </button>
          {archiveOpen && (
            <div style={{ background: 'var(--nx-surface)', borderTop: '1px solid var(--nx-border)' }}>
              {archivedMemberships.map((ms, i) => (
                <div key={ms.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: i < archivedMemberships.length - 1 ? '1px solid var(--nx-border)' : 'none' }}>
                  <div className="nx-domain-mark" style={{ width: 26, height: 26, fontSize: 10, flexShrink: 0, background: 'var(--nx-surface-alt)', color: 'var(--nx-text-muted)' }}>
                    {(ms.workspace?.name ?? '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ms.workspace?.name ?? '—'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>Assigned {fmtDate(ms.createdAt)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Pill tone="gray">{WS_STATUS_LABEL[ms.workspace?.status] ?? ms.workspace?.status}</Pill>
                    <Pill tone={ms.wsRole === 'WORKSPACE_ADMIN' ? 'purple' : 'slate'}>{WS_ROLE_LABEL[ms.wsRole] ?? ms.wsRole}</Pill>
                    {ms.wsCustomRole && <Pill tone="amber">{ms.wsCustomRole.name}</Pill>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Open in IAM (supplemental) ────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 2 }}>
        <Btn variant="ghost" size="sm" icon="ExternalLink"
          onClick={() => navigate(`/organization/${orgId}/iam?tab=member-assignments&memberId=${memberId}`)}>
          Open in Identity &amp; Access
        </Btn>
      </div>

      {/* ── Remove confirm dialog ─────────────────────────────────────── */}
      {confirmRemoveMs && (
        <ConfirmDialog
          title="Remove from Workspace"
          desc={`Remove access to "${confirmRemoveMs.workspace?.name ?? 'this workspace'}"? The member will lose access immediately.`}
          confirmLabel="Remove"
          danger
          busy={removingId === confirmRemoveMs.id}
          onConfirm={handleRemove}
          onCancel={closeConfirmRemove}
        />
      )}
    </div>
  );
}

const SESSION_STALE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days — sessions older than this are flagged

function SessionsTab({ orgId, memberId, memberStatus, isSelf, canManage, onModalChange }) {
  const toast = useToast();
  const [sessions,      setSessions]      = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [refreshing,    setRefreshing]    = useState(false);
  const [err,           setErr]           = useState(null);
  const [logoutBusy,    setLogoutBusy]    = useState(false);
  const [confirmAll,    setConfirmAll]    = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null); // holds session object
  const [revokingId,    setRevokingId]    = useState(null);

  const openModal  = useCallback(() => onModalChange?.(true),  [onModalChange]);
  const closeModal = useCallback(() => onModalChange?.(false), [onModalChange]);

  const doLoad = useCallback((silent = false) => {
    if (silent) setRefreshing(true);
    else        setLoading(true);
    setErr(null);
    let mounted = true;
    membersApi.sessions(orgId, memberId)
      .then((r)  => { if (mounted) { setSessions(Array.isArray(r) ? r : []); } })
      .catch((e) => { if (mounted) { setErr(e?.message ?? 'Failed to load sessions'); if (silent) setSessions((p) => p ?? []); } })
      .finally(()=> { if (mounted) { setLoading(false); setRefreshing(false); } });
    return () => { mounted = false; };
  }, [orgId, memberId]);

  useEffect(() => doLoad(false), [doLoad]);

  const handleForceLogoutAll = async () => {
    setConfirmAll(false);
    closeModal();
    setLogoutBusy(true);
    try {
      const res = await membersApi.forceLogout(orgId, memberId);
      const n = res?.count ?? 0;
      toast({ tone: 'success', icon: 'LogOut', title: n > 0 ? `${n} session${n !== 1 ? 's' : ''} terminated` : 'Sessions cleared' });
      setSessions([]);
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to terminate sessions', desc: e?.message });
    } finally {
      setLogoutBusy(false);
    }
  };

  const handleRevokeOne = async () => {
    if (!confirmRevoke) return;
    const s = confirmRevoke;
    setConfirmRevoke(null);
    closeModal();
    setRevokingId(s.id);
    try {
      await orgSecurityApi.revokeSession(orgId, s.id);
      setSessions((prev) => (prev ?? []).filter((x) => x.id !== s.id));
      toast({ tone: 'success', icon: 'X', title: 'Session terminated', desc: parseUA(s.userAgent) });
    } catch (e) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to terminate session', desc: e?.message });
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}>
      {[100, 85, 70].map((w, i) => (
        <div key={i} style={{ height: 56, borderRadius: 8, width: `${w}%`, background: 'var(--nx-surface-alt)', animation: 'nx-pulse 1.4s ease-in-out infinite' }} />
      ))}
    </div>
  );

  if (err) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 0', textAlign: 'center' }}>
      <NexusIcon name="AlertCircle" size={22} style={{ color: 'var(--nx-danger)' }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>Failed to load sessions</div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{err}</div>
      </div>
      <Btn variant="secondary" size="sm" icon="RefreshCw" onClick={() => doLoad(false)}>Retry</Btn>
    </div>
  );

  const list = sessions ?? [];
  const now = Date.now();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* ── Header: count + Refresh + Force Logout All ──────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)' }}>{list.length}</span>
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginLeft: 4 }}>
            active session{list.length !== 1 ? 's' : ''}
          </span>
        </div>
        <Btn
          variant="ghost"
          size="sm"
          icon="RefreshCw"
          onClick={() => doLoad(true)}
          disabled={refreshing}
          title="Refresh sessions"
        />
        {canManage && list.length > 0 && (
          <Btn
            variant="danger"
            size="sm"
            icon="LogOut"
            disabled={logoutBusy}
            onClick={() => { setConfirmAll(true); openModal(); }}
          >
            {logoutBusy ? 'Terminating…' : 'Force Logout All'}
          </Btn>
        )}
      </div>

      {/* ── isSelf notice ────────────────────────────────────────────── */}
      {isSelf && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 11px', borderRadius: 7, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <NexusIcon name="Info" size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>These are your own active sessions. Session management is not available from this view.</span>
        </div>
      )}

      {/* ── Session list / empty state ───────────────────────────────── */}
      {list.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 0', textAlign: 'center' }}>
          <NexusIcon name="Monitor" size={22} style={{ color: 'var(--nx-text-muted)' }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>No active sessions</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
              {memberStatus === 'PENDING'
                ? 'This member has not accepted their invitation yet.'
                : memberStatus === 'SUSPENDED'
                ? 'This member is suspended. All sessions have been cleared.'
                : 'This member is not currently signed in on any device.'}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((s) => {
            const ua       = s.userAgent ?? '';
            const icon     = deviceIcon(ua);
            const label    = parseUA(ua);
            const isStale  = s.createdAt && (now - new Date(s.createdAt).getTime()) > SESSION_STALE_MS;
            const isRevoking = revokingId === s.id;
            const absTime  = s.createdAt ? new Date(s.createdAt).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
            return (
              <div key={s.id} style={{ borderRadius: 8, border: '1px solid var(--nx-border)', padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                {/* Device icon */}
                <div style={{ width: 30, height: 30, borderRadius: 6, background: 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <NexusIcon name={icon} size={13} style={{ color: 'var(--nx-text-muted)' }} />
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nx-text)', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                    {isStale && <Pill tone="amber" size="xs">Stale</Pill>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 3, flexWrap: 'wrap' }}>
                    <span>IP: {s.ip || '—'}</span>
                    <span title={absTime}>Signed in: {timeAgo(s.createdAt)}</span>
                  </div>
                </div>
                {/* Per-session revoke */}
                {canManage && (
                  <button
                    type="button"
                    title="Terminate this session"
                    disabled={isRevoking}
                    onClick={() => { setConfirmRevoke(s); openModal(); }}
                    style={{ background: 'none', border: 'none', cursor: isRevoking ? 'default' : 'pointer', color: 'var(--nx-danger)', padding: '4px 6px', borderRadius: 4, display: 'flex', alignItems: 'center', flexShrink: 0, opacity: isRevoking ? 0.4 : 1 }}
                  >
                    <NexusIcon name={isRevoking ? 'Loader' : 'X'} size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Force logout all confirmation ──────────────────────────── */}
      {confirmAll && (
        <ConfirmDialog
          title="Force Logout All Sessions"
          desc={`This will terminate all ${list.length} active session${list.length !== 1 ? 's' : ''}. The member will be signed out on all devices immediately and will need to sign in again.`}
          confirmLabel="Force Logout All"
          danger
          busy={logoutBusy}
          onConfirm={handleForceLogoutAll}
          onCancel={() => { setConfirmAll(false); closeModal(); }}
        />
      )}

      {/* ── Single session revoke confirmation ─────────────────────── */}
      {confirmRevoke && (
        <ConfirmDialog
          title="Terminate Session"
          desc={`Terminate the session from "${parseUA(confirmRevoke.userAgent)}"${confirmRevoke.ip ? ` (IP: ${confirmRevoke.ip})` : ''}? The member will be signed out on that device immediately.`}
          confirmLabel="Terminate Session"
          danger
          busy={revokingId === confirmRevoke.id}
          onConfirm={handleRevokeOne}
          onCancel={() => { setConfirmRevoke(null); closeModal(); }}
        />
      )}
    </div>
  );
}

function ProfileTab({ orgId, member, detail, detailLoading, detailErr, onUpdated }) {
  const toast = useToast();

  const origName   = (member.name && member.name !== member.email) ? member.name : '';
  const origPhone  = detail?.user?.phone       ?? '';
  const origDept   = detail?.user?.department  ?? '';
  const origTz     = detail?.user?.timezone    ?? '';
  const origLocale = detail?.user?.locale      ?? '';

  const [name,   setName]   = useState(origName);
  const [phone,  setPhone]  = useState(origPhone);
  const [dept,   setDept]   = useState(origDept);
  const [tz,     setTz]     = useState(origTz);
  const [locale, setLocale] = useState(origLocale);
  const [busy,   setBusy]   = useState(false);

  // detail starts null (async fetch); re-sync fields once it arrives
  useEffect(() => {
    setPhone(detail?.user?.phone      ?? '');
    setDept(detail?.user?.department  ?? '');
    setTz(detail?.user?.timezone      ?? '');
    setLocale(detail?.user?.locale    ?? '');
  }, [detail]);

  // Trim before comparing so trailing-whitespace-only edits don't trigger dirty state
  const nameDirty    = name.trim() !== origName;
  const profileDirty = (
    phone.trim()  !== origPhone  ||
    dept.trim()   !== origDept   ||
    tz.trim()     !== origTz     ||
    locale.trim() !== origLocale
  );
  const isDirty = nameDirty || profileDirty;

  const cancel = () => {
    setName(origName);
    setPhone(origPhone);
    setDept(origDept);
    setTz(origTz);
    setLocale(origLocale);
  };

  const save = async () => {
    if (nameDirty && !name.trim()) return;
    setBusy(true);
    try {
      if (nameDirty) {
        await membersApi.update(orgId, member.id, { name: name.trim() });
      }
      if (profileDirty) {
        // Only send changed fields. Empty string → null so the backend
        // explicitly clears the DB column rather than leaving the old value.
        const patch = {};
        if (phone.trim()  !== origPhone)  patch.phone      = phone.trim()  || null;
        if (dept.trim()   !== origDept)   patch.department = dept.trim()   || null;
        if (tz.trim()     !== origTz)     patch.timezone   = tz.trim()     || null;
        if (locale.trim() !== origLocale) patch.locale     = locale.trim() || null;
        if (Object.keys(patch).length > 0) {
          await membersApi.updateProfile(orgId, member.id, patch);
        }
      }
      toast({ tone: 'success', icon: 'Check', title: 'Profile updated' });
      onUpdated();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Save failed', desc: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (e) => { if (e.key === 'Enter' && isDirty && !busy) save(); };

  // When detail loaded but member has no linked user account (common for PENDING/EXPIRED)
  const noUserAccount = !!detail && !detail.user;
  const noUserMsg = (member.status === 'PENDING' || member.status === 'EXPIRED')
    ? 'Contact and preference details are available after the member accepts their invitation.'
    : 'No user account is linked to this member.';

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{children}</div>
  );

  const FieldSkeleton = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div className="nx-skeleton" style={{ height: 13, width: 80, borderRadius: 4, flexShrink: 0 }} />
      <div className="nx-skeleton" style={{ height: 34, flex: 1, borderRadius: 4 }} />
    </div>
  );

  const NoUserNotice = () => (
    <div style={{ display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 6, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)', fontSize: 12, alignItems: 'flex-start' }}>
      <NexusIcon name="Info" size={13} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{noUserMsg}</span>
    </div>
  );

  // ProfileTab owns its scroll area AND footer. It renders as two direct flex children
  // of the drawer's column: a scrollable content div + a pinned footer div.
  // This avoids the broken sticky-inside-overflow pattern where content scrolls behind the footer.
  return (
    <>
      {/* Scrollable form content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20, minHeight: 0 }}>
        {detailErr && (
          <div style={{ padding: '8px 12px', marginBottom: 16, borderRadius: 6, background: 'rgba(220,38,38,0.06)', border: '1px solid var(--nx-danger)', color: 'var(--nx-danger)', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
            <NexusIcon name="AlertCircle" size={13} />
            <span>Could not load member details — some information may be unavailable.</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Identity — always available from the member record */}
          <div>
            <SectionLabel>Identity</SectionLabel>
            <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
              <label htmlFor="profile-email" style={{ paddingTop: 9 }}>Email</label>
              <div>
                <input id="profile-email" className="nx-input" value={member.email} disabled aria-readonly="true" />
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>Email cannot be changed from this panel.</div>
              </div>
            </div>
            <div className="nx-form-row">
              <label htmlFor="profile-name">Display Name</label>
              <input
                id="profile-name"
                className="nx-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={onEnter}
                placeholder="e.g. Jane Smith"
                maxLength={150}
              />
            </div>
          </div>

          {/* Contact & Organization — requires an accepted user account */}
          <div>
            <SectionLabel>Contact &amp; Organization</SectionLabel>
            {detailLoading ? (
              <><FieldSkeleton /><FieldSkeleton /></>
            ) : noUserAccount ? (
              <NoUserNotice />
            ) : (
              <>
                <div className="nx-form-row">
                  <label htmlFor="profile-phone">Phone</label>
                  <input id="profile-phone" className="nx-input" value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={onEnter} placeholder="e.g. +1 555 000 0000" maxLength={30} />
                </div>
                <div className="nx-form-row">
                  <label htmlFor="profile-dept">Department</label>
                  <input id="profile-dept" className="nx-input" value={dept} onChange={(e) => setDept(e.target.value)} onKeyDown={onEnter} placeholder="e.g. Engineering" maxLength={100} />
                </div>
              </>
            )}
          </div>

          {/* Preferences — requires an accepted user account */}
          <div>
            <SectionLabel>Preferences</SectionLabel>
            {detailLoading ? (
              <><FieldSkeleton /><FieldSkeleton /></>
            ) : noUserAccount ? (
              <NoUserNotice />
            ) : (
              <>
                <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
                  <label htmlFor="profile-tz" style={{ paddingTop: 9 }}>Timezone</label>
                  <div>
                    <input id="profile-tz" className="nx-input" value={tz} onChange={(e) => setTz(e.target.value)} onKeyDown={onEnter} placeholder="e.g. Asia/Kolkata" maxLength={50} />
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>IANA timezone — e.g. America/New_York, Europe/London</div>
                  </div>
                </div>
                <div className="nx-form-row" style={{ alignItems: 'flex-start' }}>
                  <label htmlFor="profile-locale" style={{ paddingTop: 9 }}>Language</label>
                  <div>
                    <input id="profile-locale" className="nx-input" value={locale} onChange={(e) => setLocale(e.target.value)} onKeyDown={onEnter} placeholder="e.g. en-US" maxLength={10} />
                    <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 4 }}>BCP 47 tag — e.g. en-US, en-IN, fr-FR</div>
                  </div>
                </div>
              </>
            )}
          </div>

        </div>
      </div>

      {/* Footer — pinned outside the scroll area; always visible regardless of form length */}
      <div style={{ flexShrink: 0, padding: '12px 20px 20px', background: 'var(--nx-bg)', borderTop: '1px solid var(--nx-border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {isDirty && <Btn variant="ghost" size="sm" onClick={cancel} disabled={busy}>Cancel</Btn>}
        <Btn variant="primary" size="sm" icon="Save" disabled={busy || !isDirty || (nameDirty && !name.trim())} onClick={save}>
          {busy ? 'Saving…' : 'Save Changes'}
        </Btn>
      </div>
    </>
  );
}

const AUDIT_TAB_LIMIT = 25;

const SEV_COLOR = { error: 'var(--nx-danger)', warning: 'var(--nx-warning)', info: 'var(--nx-accent)' };
const SEV_ICON  = { error: 'AlertCircle', warning: 'AlertTriangle', info: 'Info' };

// Map action text → meaningful icon; falls back to severity icon
function auditIcon(action) {
  if (!action) return null;
  const a = action.toLowerCase();
  if (a.includes('invited member'))                  return 'UserPlus';
  if (a.includes('reactivated member'))              return 'UserCheck';
  if (a.includes('activated member'))                return 'UserCheck';
  if (a.includes('suspended member'))                return 'UserX';
  if (a.includes('assigned member to workspace'))    return 'LayoutGrid';
  if (a.includes('workspace role') || a.includes('workspace custom role')) return 'LayoutGrid';
  if (a.includes('removed from workspace'))          return 'LayoutGrid';
  if (a.includes('removed member'))                  return 'UserMinus';
  if (a.includes('changed member role'))             return 'Shield';
  if (a.includes('assigned custom role'))            return 'Tag';
  if (a.includes('removed custom role'))             return 'Tag';
  if (a.includes('resent invite'))                   return 'Mail';
  if (a.includes('cancelled invite'))                return 'MailX';
  if (a.includes('force logged out'))                return 'LogOut';
  if (a.includes('revoked session'))                 return 'Monitor';
  if (a.includes('viewed member sessions'))          return 'Eye';
  if (a.includes('updated member name'))             return 'User';
  if (a.includes('updated member profile'))          return 'User';
  if (a.includes('batch invited'))                   return 'Users';
  return null;
}

const ROLE_LABELS = {
  ORG_ADMIN: 'Org Admin', MEMBER: 'Member', VIEWER: 'Viewer',
  WS_ADMIN: 'Workspace Admin', WS_USER: 'Workspace User', WS_VIEWER: 'Workspace Viewer',
  WORKSPACE_ADMIN: 'Workspace Admin', WORKSPACE_USER: 'Workspace User', WORKSPACE_VIEWER: 'Workspace Viewer',
};

const FIELD_LABELS = {
  orgRole: 'Org role', wsRole: 'Workspace role',
  orgCustomRoleId: 'Custom role', wsCustomRoleId: 'Custom role',
  phone: 'Phone', department: 'Department', timezone: 'Timezone', locale: 'Locale',
  status: 'Status', name: 'Name', email: 'Email',
};

function humanizeRole(val) {
  if (!val) return '—';
  return ROLE_LABELS[val] ?? val;
}

function humanizeField(key) {
  return FIELD_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
}

function humanizeValue(key, val) {
  if (val == null) return '—';
  const s = String(val);
  if (key === 'orgRole' || key === 'wsRole') return humanizeRole(s);
  if (key === 'status') {
    const STATUS_LABELS = { ACTIVE: 'Active', SUSPENDED: 'Suspended', PENDING: 'Pending', INVITED: 'Invited', EXPIRED: 'Expired' };
    return STATUS_LABELS[s] ?? s;
  }
  return s;
}

function humanizeAction(action) {
  if (!action) return 'Unknown action';
  const a = action.toLowerCase();
  if (a.includes('invited member'))               return 'Member invited';
  if (a.includes('reactivated member'))           return 'Member reactivated';
  if (a.includes('activated member'))             return 'Member activated';
  if (a.includes('suspended member'))             return 'Member suspended';
  if (a.includes('assigned member to workspace')) return 'Added to workspace';
  if (a.includes('workspace custom role'))        return 'Workspace custom role changed';
  if (a.includes('workspace role'))               return 'Workspace role changed';
  if (a.includes('removed from workspace'))       return 'Removed from workspace';
  if (a.includes('removed member'))               return 'Member removed';
  if (a.includes('changed member role'))          return 'Org role changed';
  if (a.includes('assigned custom role'))         return 'Custom role assigned';
  if (a.includes('removed custom role'))          return 'Custom role removed';
  if (a.includes('resent invite'))                return 'Invite resent';
  if (a.includes('cancelled invite'))             return 'Invite cancelled';
  if (a.includes('force logged out'))             return 'All sessions terminated';
  if (a.includes('revoked session'))              return 'Session revoked';
  if (a.includes('viewed member sessions'))       return 'Sessions viewed';
  if (a.includes('updated member name'))          return 'Name updated';
  if (a.includes('updated member profile'))       return 'Profile updated';
  if (a.includes('batch invited'))                return 'Batch invite';
  if (a.includes('password reset'))               return 'Password reset sent';
  return action.replace(/\b\w/g, c => c.toUpperCase());
}

function collapseNoise(entries) {
  const result = [];
  let i = 0;
  while (i < entries.length) {
    const e = entries[i];
    if (e.action === 'viewed member sessions') {
      let count = 1;
      while (i + count < entries.length && entries[i + count].action === 'viewed member sessions') count++;
      result.push({ ...e, _noiseCount: count });
      i += count;
    } else {
      result.push(e);
      i++;
    }
  }
  return result;
}

// Format a display name from an email address or full name string.
// "sunil.kudumu@nuacem.com" → "Sunil Kudumu"   "hari.k@nuacem.com" → "Hari K"
function formatDisplayName(emailOrName) {
  if (!emailOrName) return 'System';
  if (!emailOrName.includes('@')) return emailOrName;
  const local = emailOrName.split('@')[0];
  const parts = local.split(/[._-]/);
  return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' ');
}

// Compact key-value metadata row used throughout the audit entry layout.
function MetaRow({ label, value, tooltip, mono }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 0, fontSize: 11, lineHeight: 1.7 }}>
      <span style={{ color: 'var(--nx-text-muted)', width: 120, flexShrink: 0 }}>{label}</span>
      <span title={tooltip ?? undefined} style={{ color: 'var(--nx-text)', fontFamily: mono ? 'monospace' : undefined }}>
        {value}
      </span>
    </div>
  );
}

function renderAuditDiff(action, entityBefore, entityAfter) {
  const a = (action ?? '').toLowerCase();
  const hasBefore = entityBefore && typeof entityBefore === 'object' && !Array.isArray(entityBefore) && Object.keys(entityBefore).length > 0;
  const hasAfter  = entityAfter  && typeof entityAfter  === 'object' && !Array.isArray(entityAfter)  && Object.keys(entityAfter).length  > 0;
  const isRoleChange = a.includes('role') || a.includes('assigned custom') || a.includes('removed custom');

  if (isRoleChange && !hasBefore && !hasAfter) {
    return (
      <MetaRow
        label="Note"
        value={
          <span style={{ color: 'var(--nx-text-muted)', fontStyle: 'italic' }}>
            Historical audit record — role transition not available
          </span>
        }
      />
    );
  }

  if (isRoleChange && (hasBefore || hasAfter)) {
    const ROLE_KEYS = ['orgRole', 'wsRole', 'orgCustomRoleId', 'wsCustomRoleId'];
    const roleKey = ROLE_KEYS.find(k =>
      (hasBefore && entityBefore[k] !== undefined) || (hasAfter && entityAfter[k] !== undefined)
    ) ?? (hasBefore ? Object.keys(entityBefore)[0] : Object.keys(entityAfter)[0]);
    const fromVal = hasBefore && entityBefore[roleKey] != null ? String(entityBefore[roleKey]) : null;
    const toVal   = hasAfter  && entityAfter[roleKey]  != null ? String(entityAfter[roleKey])  : null;
    const pill = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {fromVal && (
          <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 20, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)', fontWeight: 500 }}>
            {humanizeRole(fromVal)}
          </span>
        )}
        {fromVal && toVal && (
          <NexusIcon name="ArrowRight" size={11} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
        )}
        {!fromVal && toVal && <span style={{ color: 'var(--nx-text-muted)' }}>Assigned as</span>}
        {toVal && (
          <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 20, background: 'var(--nx-accent)1a', border: '1px solid var(--nx-accent)33', color: 'var(--nx-accent)', fontWeight: 600 }}>
            {humanizeRole(toVal)}
          </span>
        )}
      </div>
    );
    return <MetaRow label="Role" value={pill} />;
  }

  if (!hasBefore && !hasAfter) return null;

  const keys = [...new Set([...(hasBefore ? Object.keys(entityBefore) : []), ...(hasAfter ? Object.keys(entityAfter) : [])])];
  return (
    <>
      {keys.map(k => {
        const bv = hasBefore ? entityBefore[k] : undefined;
        const av = hasAfter  ? entityAfter[k]  : undefined;
        const changed = bv !== av;
        const val = (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {bv !== undefined && (
              <span style={{ color: 'var(--nx-text-muted)', textDecoration: changed ? 'line-through' : 'none', opacity: changed ? 0.7 : 1 }}>
                {humanizeValue(k, bv)}
              </span>
            )}
            {changed && bv !== undefined && av !== undefined && (
              <NexusIcon name="ArrowRight" size={10} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
            )}
            {av !== undefined && changed && (
              <span style={{ color: 'var(--nx-text)', fontWeight: 500 }}>{humanizeValue(k, av)}</span>
            )}
          </div>
        );
        return <MetaRow key={k} label={humanizeField(k)} value={val} />;
      })}
    </>
  );
}

// Categories that carry member-relevant events:
// Member — all direct member lifecycle events (invite, suspend, role, profile, sessions)
// IAM    — workspace assignments/role changes (target fixed to include email in backend)
// Security — individual session revocations (target fixed to member.email in backend)
const AUDIT_MEMBER_CATS = 'Member,IAM,Security';

function AuditTab({ orgId, member }) {
  const [entries,     setEntries]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [fetchErr,    setFetchErr]    = useState(null);
  const [loadMoreErr, setLoadMoreErr] = useState(null);
  const [page,        setPage]        = useState(1);
  const [hasMore,     setHasMore]     = useState(false);
  const [total,       setTotal]       = useState(null);

  const doLoad = useCallback((silent = false) => {
    if (silent) setRefreshing(true);
    else        setLoading(true);
    setFetchErr(null);
    let mounted = true;
    auditApi.listByOrg(orgId, { cats: AUDIT_MEMBER_CATS, target: member.email, limit: AUDIT_TAB_LIMIT, page: 1 })
      .then((r) => {
        if (!mounted) return;
        const rows = r.data ?? [];
        setEntries(rows);
        setPage(1);
        setHasMore(rows.length >= AUDIT_TAB_LIMIT);
        setTotal(r.total ?? null);
      })
      .catch((e) => { if (mounted) setFetchErr(e?.message ?? 'Failed to load audit events'); })
      .finally(() => { if (mounted) { setLoading(false); setRefreshing(false); } });
    return () => { mounted = false; };
  }, [orgId, member.email]);

  useEffect(() => doLoad(false), [doLoad]);

  const loadMore = () => {
    if (loading) return;
    const nextPage = page + 1;
    setLoading(true);
    setLoadMoreErr(null);
    auditApi.listByOrg(orgId, { cats: AUDIT_MEMBER_CATS, target: member.email, limit: AUDIT_TAB_LIMIT, page: nextPage })
      .then((r) => {
        const rows = r.data ?? [];
        setEntries((prev) => [...prev, ...rows]);
        setPage(nextPage);
        setHasMore(rows.length >= AUDIT_TAB_LIMIT);
      })
      .catch((e) => setLoadMoreErr(e?.message ?? 'Failed to load more'))
      .finally(() => setLoading(false));
  };

  /* ── Loading skeleton ─────────────────────────────────────────── */
  if (loading && entries.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}>
      {[100, 90, 75].map((w, i) => (
        <div key={i} style={{ height: 52, borderRadius: 8, width: `${w}%`, background: 'var(--nx-surface-alt)', animation: 'nx-pulse 1.4s ease-in-out infinite' }} />
      ))}
    </div>
  );

  /* ── Error with retry ─────────────────────────────────────────── */
  if (fetchErr && entries.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 0', textAlign: 'center' }}>
      <NexusIcon name="AlertCircle" size={22} style={{ color: 'var(--nx-danger)' }} />
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>Failed to load audit events</div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{fetchErr}</div>
      </div>
      <Btn variant="secondary" size="sm" icon="RefreshCw" onClick={() => doLoad(false)}>Retry</Btn>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Header: count + Refresh ─────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          {total !== null && (
            <>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)' }}>{total}</span>
              <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginLeft: 4 }}>
                event{total !== 1 ? 's' : ''}
              </span>
            </>
          )}
        </div>
        <Btn
          variant="ghost"
          size="sm"
          icon="RefreshCw"
          disabled={refreshing}
          onClick={() => doLoad(true)}
          title="Refresh audit timeline"
        />
      </div>

      {/* ── Empty state ──────────────────────────────────────────── */}
      {entries.length === 0 && !loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 0', textAlign: 'center' }}>
          <NexusIcon name="ClipboardList" size={22} style={{ color: 'var(--nx-text-muted)' }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>No audit events found</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
              {member.status === 'PENDING'
                ? 'This member has not performed any actions yet.'
                : 'No recorded activity for this member.'}
            </div>
          </div>
        </div>
      ) : (
        /* ── Timeline ──────────────────────────────────────────── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {(() => {
            const mDisplay = member.name || formatDisplayName(member.email);
            return collapseNoise(entries).map((e, i, arr) => {
              const isNoise    = e.action === 'viewed member sessions';
              const noiseCount = e._noiseCount ?? 1;
              const sevColor   = SEV_COLOR[e.sev] ?? 'var(--nx-accent)';
              const iconName   = auditIcon(e.action) ?? SEV_ICON[e.sev] ?? 'ClipboardList';
              const isLast     = i === arr.length - 1;
              const absTime    = e.createdAt
                ? new Date(e.createdAt).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                : '';
              const title         = humanizeAction(e.action) + (noiseCount > 1 ? ` ×${noiseCount}` : '');
              const actorDisplay  = formatDisplayName(e.who);
              const targetContext = (e.target && e.target !== member.email)
                ? e.target.replace(member.email, '').trim()
                : null;
              const diff = isNoise ? null : renderAuditDiff(e.action, e.entityBefore, e.entityAfter);

              return (
                <div
                  key={e.id ?? i}
                  style={{ display: 'flex', gap: 10, padding: isNoise ? '5px 0' : '11px 0', borderBottom: isLast ? 'none' : '1px solid var(--nx-border)', opacity: isNoise ? 0.4 : 1 }}
                >
                  {/* Icon dot */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ width: isNoise ? 20 : 28, height: isNoise ? 20 : 28, borderRadius: '50%', background: isNoise ? 'var(--nx-surface-alt)' : `${sevColor}14`, border: isNoise ? '1px solid var(--nx-border)' : `1.5px solid ${sevColor}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <NexusIcon name={iconName} size={isNoise ? 9 : 13} style={{ color: isNoise ? 'var(--nx-text-muted)' : sevColor }} />
                    </div>
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>

                    {/* Event title + timestamp */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: isNoise ? 12 : 13, fontWeight: isNoise ? 400 : 600, color: isNoise ? 'var(--nx-text-muted)' : 'var(--nx-text)', lineHeight: 1.35 }}>
                        {title}
                      </span>
                      <span title={absTime} style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', flexShrink: 0, cursor: 'default', marginTop: 1 }}>
                        {e.time ?? fmtDate(e.createdAt)}
                      </span>
                    </div>

                    {/* Severity badge — warning/error only */}
                    {!isNoise && e.sev && e.sev !== 'info' && (
                      <div style={{ marginTop: 2 }}>
                        <span style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, background: `${sevColor}18`, color: sevColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {e.sev}
                        </span>
                      </div>
                    )}

                    {/* Structured metadata block */}
                    {!isNoise && (
                      <div style={{ marginTop: 5 }}>
                        {e.who && <MetaRow label="Performed by" value={actorDisplay} tooltip={e.who} />}
                        <MetaRow label="Affected member" value={mDisplay} tooltip={member.email} />
                        {targetContext && <MetaRow label="Workspace" value={targetContext} />}
                        {diff}
                        {e.ip && <MetaRow label="Source IP" value={e.ip} mono tooltip={`Source address: ${e.ip}`} />}
                      </div>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ── Load more ────────────────────────────────────────────── */}
      {loading && entries.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <NexusIcon name="Loader" size={13} /><span>Loading more…</span>
        </div>
      )}
      {!loading && loadMoreErr && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', fontSize: 12 }}>
          <NexusIcon name="AlertCircle" size={13} style={{ color: 'var(--nx-danger)' }} />
          <span style={{ color: 'var(--nx-danger)', flex: 1 }}>{loadMoreErr}</span>
          <Btn variant="ghost" size="sm" onClick={loadMore}>Retry</Btn>
        </div>
      )}
      {!loading && hasMore && !loadMoreErr && (
        <div style={{ textAlign: 'center', padding: '10px 0' }}>
          <Btn variant="ghost" size="sm" onClick={loadMore}>Load more</Btn>
        </div>
      )}
    </div>
  );
}

const ROLE_DESC = {
  ORG_ADMIN: 'Full organization administration — manage members, workspaces, roles, and settings',
  MEMBER:    'Standard member — access assigned workspaces with CRM capabilities',
  VIEWER:    'Read-only — view members, workspaces, and organization data',
};

function RoleSummaryTab({
  orgId, member, detail, memberships, detailLoading, detailErr, canManage,
  orgCustomRoles, orgCustomRolesLoading, newRole, onRoleChange,
  newCustomRoleId, onCustomRoleChange, onApplyRoles, rolesSaving,
  onOpenIAM, onNavigateIAMRoles, onResendInvite, resendBusy,
}) {
  const navigate = useNavigate();

  const isPending   = member.status === 'PENDING';
  const isSuspended = member.status === 'SUSPENDED';
  const isExpired   = member.status === 'EXPIRED';
  const customRole  = detail?.orgCustomRole ?? null;
  const roleDirty   = newRole !== member.orgRole;
  const customDirty = newCustomRoleId !== (customRole?.id ?? '');
  const anyDirty    = roleDirty || customDirty;
  const activeWsMemberships = (memberships ?? []).filter((m) => m.workspace?.status === 'ACTIVE');

  const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{children}</div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Status context banners */}
      {isPending && (
        <div role="status" style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', fontSize: 13 }}>
          <NexusIcon name="Mail" size={15} style={{ color: 'var(--nx-warning)', flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>Role pending acceptance</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>This role becomes active once the member accepts their invitation.</div>
            {canManage && onResendInvite && (
              <div style={{ marginTop: 8 }}>
                <Btn variant="secondary" size="sm" icon="Mail" disabled={resendBusy} onClick={onResendInvite}>
                  {resendBusy ? 'Sending…' : 'Resend Invitation'}
                </Btn>
              </div>
            )}
          </div>
        </div>
      )}
      {isSuspended && (
        <div role="status" style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.30)', fontSize: 13 }}>
          <NexusIcon name="PauseCircle" size={15} style={{ color: 'var(--nx-danger)', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontWeight: 600 }}>Role suspended</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>This member cannot access the system. You can still update their role — changes take effect when the account is activated.</div>
          </div>
        </div>
      )}
      {isExpired && (
        <div role="status" style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', fontSize: 13 }}>
          <NexusIcon name="Clock" size={15} style={{ color: 'var(--nx-text-muted)', flexShrink: 0, marginTop: 1 }} />
          <div>
            <div style={{ fontWeight: 600 }}>Invitation expired</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>This role is assigned but not yet active. Remove this member and send a new invitation to restore access.</div>
          </div>
        </div>
      )}

      {/* Organization Base Role */}
      <div>
        <SectionLabel>Organization Role</SectionLabel>
        <div style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'color-mix(in srgb, var(--nx-accent) 10%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <NexusIcon name="Shield" size={17} style={{ color: 'var(--nx-accent)' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{ROLE_LABEL[member.orgRole] ?? member.orgRole}</div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2, lineHeight: 1.5 }}>{ROLE_DESC[member.orgRole] ?? 'Organization role'}</div>
            </div>
            <Pill tone={roleTone(member.orgRole)}>{roleFmt(member.orgRole)}</Pill>
          </div>
          {/* Inline role editor — only for active, manageable members */}
          {canManage && !isPending && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--nx-border)' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>Change to:</span>
              <select
                className="nx-input"
                style={{ flex: 1, fontSize: 12 }}
                value={newRole}
                onChange={(e) => onRoleChange(e.target.value)}
                disabled={rolesSaving}
                aria-label="Change organization role"
              >
                {ROLE_OPTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Custom Role */}
      <div>
        <SectionLabel>Custom Role</SectionLabel>
        {detailLoading ? (
          <div style={{ padding: '12px 14px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="nx-skeleton" style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
              <div className="nx-skeleton" style={{ height: 13, width: 120, borderRadius: 4 }} />
              <div className="nx-skeleton" style={{ height: 11, width: 200, borderRadius: 4 }} />
            </div>
          </div>
        ) : detailErr ? (
          <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', border: '1px solid var(--nx-danger)', color: 'var(--nx-danger)', fontSize: 12, alignItems: 'center' }}>
            <NexusIcon name="AlertCircle" size={13} />
            <span>Could not load custom role — member details unavailable.</span>
          </div>
        ) : (
          <div style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${customRole ? 'color-mix(in srgb, var(--nx-accent) 25%, transparent)' : 'var(--nx-border)'}`, background: customRole ? 'color-mix(in srgb, var(--nx-accent) 4%, transparent)' : 'var(--nx-bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: customRole ? 'color-mix(in srgb, var(--nx-accent) 10%, transparent)' : 'var(--nx-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <NexusIcon name="Tag" size={17} style={{ color: customRole ? 'var(--nx-accent)' : 'var(--nx-text-muted)' }} />
              </div>
              {customRole ? (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{customRole.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                    Custom role · Applies across this organization
                  </div>
                </div>
              ) : (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--nx-text-muted)' }}>No custom role</div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>Using base role permissions only</div>
                </div>
              )}
              {customRole && <Pill tone="purple">Custom</Pill>}
            </div>
            {/* Inline custom role editor — hidden for PENDING (consistent with base role editor) */}
            {canManage && !isPending && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--nx-border)' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>Assign:</span>
                {orgCustomRolesLoading ? (
                  <div className="nx-skeleton" style={{ flex: 1, height: 30, borderRadius: 4 }} />
                ) : orgCustomRoles.length === 0 ? (
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--nx-text-muted)' }}>
                    No custom roles defined.{' '}
                    <button
                      style={{ background: 'none', border: 'none', color: 'var(--nx-accent)', cursor: 'pointer', fontSize: 12, padding: 0, textDecoration: 'underline' }}
                      onClick={onNavigateIAMRoles ?? (() => navigate(`/organization/${orgId}/iam?tab=roles`))}
                    >
                      Create one in Identity &amp; Access
                    </button>.
                  </span>
                ) : (
                  <>
                    <select
                      className="nx-input"
                      style={{ flex: 1, fontSize: 12 }}
                      value={newCustomRoleId}
                      onChange={(e) => onCustomRoleChange(e.target.value)}
                      disabled={rolesSaving}
                      aria-label="Assign custom role"
                    >
                      <option value="">— No custom role —</option>
                      {orgCustomRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Apply Changes — single save for both org role + custom role */}
      {canManage && !isPending && anyDirty && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.30)', fontSize: 12 }}>
          <NexusIcon name="AlertCircle" size={14} style={{ color: rolesSaving ? 'var(--nx-text-muted)' : 'var(--nx-warning)', flexShrink: 0 }} />
          <span style={{ flex: 1, color: 'var(--nx-text-muted)' }}>
            {rolesSaving ? 'Saving changes…' : 'Role changes have not been saved yet.'}
          </span>
          <Btn variant="primary" size="sm" disabled={rolesSaving} onClick={onApplyRoles}>
            {rolesSaving ? 'Saving…' : 'Apply Changes'}
          </Btn>
        </div>
      )}

      {/* Workspace Memberships */}
      <div>
        <SectionLabel>
          Workspace Memberships{!detailLoading && !detailErr && activeWsMemberships.length > 0 ? ` (${activeWsMemberships.length})` : ''}
        </SectionLabel>
        {detailLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[0, 1].map((i) => (
              <div key={i} className="nx-skeleton" style={{ height: 44, borderRadius: 8 }} />
            ))}
          </div>
        ) : detailErr ? (
          <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', border: '1px solid var(--nx-danger)', color: 'var(--nx-danger)', fontSize: 12, alignItems: 'center' }}>
            <NexusIcon name="AlertCircle" size={13} />
            <span>Could not load workspace memberships.</span>
          </div>
        ) : activeWsMemberships.length === 0 ? (
          <div style={{ display: 'flex', gap: 8, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)', fontSize: 12, alignItems: 'center' }}>
            <NexusIcon name="LayoutGrid" size={13} style={{ flexShrink: 0 }} />
            {member.orgRole === 'ORG_ADMIN'
              ? <span>Organization Admins have full access without workspace assignments.</span>
              : <span>Not assigned to any workspaces. Use the Workspaces tab to add assignments.</span>
            }
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activeWsMemberships.slice(0, 5).map((ms) => (
              <div key={ms.id ?? ms.workspace?.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'var(--nx-bg)', fontSize: 13 }}>
                <NexusIcon name="LayoutGrid" size={13} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ms.workspace?.name ?? '—'}</span>
                <Pill tone={ms.wsRole === 'WORKSPACE_ADMIN' ? 'purple' : 'slate'}>{WS_ROLE_LABEL[ms.wsRole] ?? ms.wsRole}</Pill>
                {ms.wsCustomRole && <Pill tone="purple">{ms.wsCustomRole.name}</Pill>}
              </div>
            ))}
            {activeWsMemberships.length > 5 && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--nx-text-muted)', background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 20, padding: '3px 12px' }}>
                  +{activeWsMemberships.length - 5} more · open the Workspaces tab to view all
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* IAM link */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)' }}>
        {!customRole ? (
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
            Custom roles are created and managed in <strong>Identity &amp; Access → Roles</strong>.
          </div>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
            Manage workspace assignments and permissions in <strong>Identity &amp; Access</strong>.
          </div>
        )}
        <Btn variant="secondary" size="sm" icon="ExternalLink" onClick={onOpenIAM ?? (() => navigate(`/organization/${orgId}/iam?tab=member-assignments&memberId=${member.id}`))}>
          Open IAM
        </Btn>
      </div>

    </div>
  );
}

/* ── Permissions tab helpers ─────────────────────────────────────────────── */

function CapBadge({ cap, source }) {
  const bg  = source === 'custom' ? 'var(--nx-warning-tint)'
            : source === 'ws'     ? 'color-mix(in srgb, var(--nx-accent) 5%, var(--nx-surface-alt))'
            :                       'color-mix(in srgb, var(--nx-accent) 8%, transparent)';
  const bdr = source === 'custom' ? 'color-mix(in srgb, var(--nx-warning) 28%, transparent)'
            : source === 'ws'     ? 'var(--nx-border)'
            :                       'color-mix(in srgb, var(--nx-accent) 20%, transparent)';
  return (
    <span title={cap} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 5, background: bg, border: `1px solid ${bdr}`, fontSize: 11, fontWeight: 500, color: 'var(--nx-text)', whiteSpace: 'nowrap' }}>
      <NexusIcon name="Check" size={9} style={{ color: 'var(--nx-success)', flexShrink: 0 }} />
      {CAP_LABELS[cap] ?? cap}
    </span>
  );
}

function CapGroup({ prefix, caps, source }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        <span>{CAP_GROUP_LABELS[prefix] ?? prefix}</span>
        <span style={{ fontWeight: 400 }}>({caps.length})</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {caps.map((cap) => <CapBadge key={cap} cap={cap} source={source} />)}
      </div>
    </div>
  );
}

function WsCapRow({ ws, orgCaps }) {
  const [open, setOpen] = useState(false);
  const caps     = ws.capabilities ?? [];
  /* How many capabilities this workspace grants that the member's org role doesn't already cover */
  const newCnt   = caps.filter((c) => !orgCaps.includes(c)).length;
  const groups   = groupCapabilities(caps);
  return (
    <div style={{ borderRadius: 8, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--nx-surface-alt)', border: 'none', cursor: 'pointer', textAlign: 'left', minWidth: 0 }}
      >
        <NexusIcon name="LayoutGrid" size={13} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
        <span style={{ flex: 1, fontWeight: 600, fontSize: 12, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {ws.workspace?.name ?? '—'}
        </span>
        <span style={{ flexShrink: 0 }}>
          <Pill tone={ws.wsRole === 'WORKSPACE_ADMIN' ? 'purple' : 'slate'}>
            {WS_ROLE_LABEL[ws.wsRole] ?? ws.wsRole}
          </Pill>
        </span>
        {ws.wsCustomRole && (
          <span style={{ flexShrink: 0 }}>
            <Pill tone="amber">{ws.wsCustomRole.name}</Pill>
          </span>
        )}
        <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
          {caps.length} cap{caps.length !== 1 ? 's' : ''}{newCnt > 0 ? ` · +${newCnt} new` : ''}
        </span>
        <NexusIcon name={open ? 'ChevronUp' : 'ChevronDown'} size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{ padding: '10px 12px', background: 'var(--nx-surface)', borderTop: '1px solid var(--nx-border)' }}>
          {caps.length === 0
            ? <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No capabilities for this workspace assignment.</span>
            : (
              <>
                {ws.wsCustomRole && (
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <NexusIcon name="Tag" size={11} style={{ color: 'var(--nx-warning)', flexShrink: 0 }} />
                    <span>Custom role <strong style={{ color: 'var(--nx-text)' }}>{ws.wsCustomRole.name}</strong> adds to the workspace base role.</span>
                  </div>
                )}
                {Object.entries(groups).map(([pfx, grpCaps]) => (
                  <CapGroup key={pfx} prefix={pfx} caps={grpCaps} source="ws" />
                ))}
                {newCnt === 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--nx-text-muted)' }}>
                    All of these capabilities are already granted by the member's organization role.
                  </div>
                )}
              </>
            )
          }
        </div>
      )}
    </div>
  );
}

function PermissionSummaryTab({ orgId, memberId }) {
  const navigate = useNavigate();

  /* ── State — ALL hooks before any early return ─────────────────────────── */
  const [access,       setAccess]       = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  /* null = smart default (collapsed when >6 caps); boolean = user's explicit choice */
  const [baseExpanded, setBaseExpanded] = useState(null);
  const [capSearch,    setCapSearch]    = useState('');

  /* ── Reset accordion + search when member changes ──────────────────────── */
  useEffect(() => {
    setBaseExpanded(null);
    setCapSearch('');
  }, [orgId, memberId]);

  /* ── Fetch effective access ─────────────────────────────────────────────── */
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    orgRolesApi.getEffectiveAccess(orgId, memberId)
      .then((r)    => { if (mounted) setAccess(r); })
      .catch((err) => { if (mounted) setError(err?.message ?? 'Failed to load permissions'); })
      .finally(()  => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [orgId, memberId, refreshCount]);

  const onRefresh = useCallback(() => setRefreshCount((c) => c + 1), []);

  /* ── Memos that depend on `access` — must be before early returns ──────── */
  const finalCapsAll = useMemo(() => access?.permissionSource?.final ?? [], [access]);

  const filteredFinalCaps = useMemo(() => {
    const q = capSearch.trim().toLowerCase();
    if (!q) return finalCapsAll;
    return finalCapsAll.filter((c) => c.toLowerCase().includes(q) || (CAP_LABELS[c] ?? '').toLowerCase().includes(q));
  }, [finalCapsAll, capSearch]);

  const finalCapGroups = useMemo(() => groupCapabilities(filteredFinalCaps), [filteredFinalCaps]);

  /* ── Early returns ─────────────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
      {[75, 100, 55, 90, 40].map((w, i) => (
        <div key={i} style={{ height: 14, borderRadius: 6, width: `${w}%`, background: 'var(--nx-surface-alt)', animation: 'nx-pulse 1.4s ease-in-out infinite' }} />
      ))}
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '28px 0' }}>
      <NexusIcon name="AlertCircle" size={22} style={{ color: 'var(--nx-danger)' }} />
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--nx-text)' }}>{error}</span>
      <Btn variant="secondary" size="sm" icon="RefreshCw" onClick={onRefresh}>Retry</Btn>
    </div>
  );

  /* ── Derived values — access is non-null here ───────────────────────────── */
  const orgCaps       = access.orgPermissions?.capabilities       ?? [];
  const orgSystemCaps = access.orgPermissions?.systemCapabilities ?? orgCaps;
  const finalCaps     = finalCapsAll;
  const wsAssignments = access.workspaceAssignments               ?? [];
  const baseRole      = access.orgPermissions?.systemRole         ?? access.member?.orgRole ?? '—';
  const customRole    = access.orgPermissions?.customRole;
  const memberStatus  = access.member?.status;

  /* Delta: what the custom role adds beyond the base role */
  const customRolePerms = Array.isArray(customRole?.permissions) ? customRole.permissions : [];
  const deltaCaps       = customRole ? customRolePerms.filter((p) => !orgSystemCaps.includes(p)) : [];
  const baseGroups      = groupCapabilities(orgSystemCaps);

  /* Workspace-exclusive capabilities: granted by workspaces but NOT by the org role */
  const allWsCaps  = wsAssignments.flatMap((w) => w.capabilities ?? []);
  const wsOnlyCaps = [...new Set(allWsCaps)].filter((c) => !orgCaps.includes(c));

  const showBaseExpanded = baseExpanded !== null ? baseExpanded : orgSystemCaps.length <= 6;
  const isSearching      = capSearch.trim().length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Suspended member warning ──────────────────────────────── */}
      {memberStatus === 'SUSPENDED' && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 8, background: 'var(--nx-warning-tint)', border: '1px solid color-mix(in srgb, var(--nx-warning) 35%, transparent)', fontSize: 12 }}>
          <NexusIcon name="AlertTriangle" size={14} style={{ color: 'var(--nx-warning)', flexShrink: 0, marginTop: 1 }} />
          <span style={{ color: 'var(--nx-text)' }}>
            <strong>Member suspended.</strong> These capabilities are currently blocked — the member cannot authenticate or access any resources until reactivated.
          </span>
        </div>
      )}

      {/* ── Hero: total effective access ──────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--nx-text)', lineHeight: 1.2, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ color: memberStatus === 'SUSPENDED' ? 'var(--nx-text-muted)' : 'var(--nx-text)' }}>{finalCaps.length}</span>
            <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--nx-text-muted)' }}>
              effective {finalCaps.length === 1 ? 'capability' : 'capabilities'}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 3 }}>
            {deltaCaps.length > 0
              ? `${orgSystemCaps.length} base + ${deltaCaps.length} custom = ${orgCaps.length} at org level`
              : `${orgCaps.length} from org role`}
            {wsOnlyCaps.length > 0 && ` · +${wsOnlyCaps.length} from workspaces`}
            {wsAssignments.length > 0 && wsOnlyCaps.length === 0 && ` · ${wsAssignments.length} workspace${wsAssignments.length !== 1 ? 's' : ''} (no new capabilities)`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'flex-start', marginTop: 2 }}>
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={onRefresh} title="Refresh permissions" />
          <Btn variant="secondary" size="sm" icon="ExternalLink"
            onClick={() => navigate(`/organization/${orgId}/iam?tab=member-assignments&memberId=${memberId}`)}>
            Identity &amp; Access
          </Btn>
        </div>
      </div>

      {/* ── Section: Complete Effective Access ────────────────────── */}
      <div style={{ borderRadius: 8, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
        {/* Card header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--nx-surface-alt)', borderBottom: '1px solid var(--nx-border)' }}>
          <NexusIcon name="Key" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--nx-text)' }}>
            Complete Effective Access
          </span>
          <span style={{ flexShrink: 0 }}>
            <Pill tone="green">{finalCaps.length} total</Pill>
          </span>
        </div>
        {/* Search */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--nx-surface)' }}>
          <NexusIcon name="Search" size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
          <input
            value={capSearch}
            onChange={(e) => setCapSearch(e.target.value)}
            placeholder="Search capabilities by name or key…"
            className="nx-input--inline"
            style={{ flex: 1, fontSize: 12, background: 'transparent', border: 'none', outline: 'none', color: 'var(--nx-text)' }}
          />
          {isSearching && (
            <>
              <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', flexShrink: 0 }}>
                {filteredFinalCaps.length}/{finalCaps.length}
              </span>
              <button type="button" onClick={() => setCapSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--nx-text-muted)' }}>
                <NexusIcon name="X" size={12} />
              </button>
            </>
          )}
        </div>
        {/* Capability groups */}
        <div style={{ padding: '10px 12px', background: 'var(--nx-surface)' }}>
          {filteredFinalCaps.length === 0
            ? (
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', padding: '8px 0', textAlign: 'center' }}>
                {isSearching ? `No capabilities matching "${capSearch}"` : 'No capabilities assigned.'}
              </div>
            )
            : Object.entries(finalCapGroups).map(([prefix, caps]) => (
              <div key={prefix} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span>{CAP_GROUP_LABELS[prefix] ?? prefix}</span>
                  <span style={{ fontWeight: 400 }}>({caps.length})</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {caps.map((cap) => (
                    <span
                      key={cap}
                      title={`${CAP_LABELS[cap] ?? cap} · ${cap}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 5, background: 'color-mix(in srgb, var(--nx-accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--nx-accent) 20%, transparent)', fontSize: 11, fontWeight: 500, color: 'var(--nx-text)', whiteSpace: 'nowrap' }}
                    >
                      <NexusIcon name="Check" size={9} style={{ color: 'var(--nx-success)', flexShrink: 0 }} />
                      {CAP_LABELS[cap] ?? cap}
                    </span>
                  ))}
                </div>
              </div>
            ))
          }
        </div>
      </div>

      {/* ── Divider ───────────────────────────────────────────────── */}
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Permission Sources
      </div>

      {/* ── Base role block ───────────────────────────────────────── */}
      <div style={{ borderRadius: 8, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setBaseExpanded(!showBaseExpanded)}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--nx-surface-alt)', border: 'none', cursor: 'pointer', textAlign: 'left' }}
        >
          <NexusIcon name="Shield" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text)' }}>
              {ROLE_LABEL[baseRole] ?? baseRole}
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--nx-text-muted)', marginLeft: 6 }}>base role</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>
              {orgSystemCaps.length} {orgSystemCaps.length === 1 ? 'capability' : 'capabilities'} granted by this role
            </div>
          </div>
          <span style={{ flexShrink: 0 }}><Pill tone={roleTone(baseRole)}>{ROLE_LABEL[baseRole] ?? baseRole}</Pill></span>
          <NexusIcon name={showBaseExpanded ? 'ChevronUp' : 'ChevronDown'} size={12} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
        </button>
        {showBaseExpanded && (
          <div style={{ padding: '10px 12px', background: 'var(--nx-surface)', borderTop: '1px solid var(--nx-border)' }}>
            {orgSystemCaps.length > 0
              ? Object.entries(baseGroups).map(([pfx, caps]) => (
                  <CapGroup key={pfx} prefix={pfx} caps={caps} source="base" />
                ))
              : <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No capabilities for this role.</span>
            }
          </div>
        )}
      </div>

      {/* ── Custom role block ─────────────────────────────────────── */}
      {customRole ? (
        <div style={{ borderRadius: 8, border: '1px solid color-mix(in srgb, var(--nx-warning) 35%, transparent)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'color-mix(in srgb, var(--nx-warning) 6%, var(--nx-surface-alt))' }}>
            <NexusIcon name="Tag" size={14} style={{ color: 'var(--nx-warning)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--nx-text)' }}>
                {customRole.name}
                <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--nx-text-muted)', marginLeft: 6 }}>custom role</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>
                {deltaCaps.length > 0
                  ? `Adds ${deltaCaps.length} ${deltaCaps.length === 1 ? 'capability' : 'capabilities'} beyond the base role`
                  : 'No new capabilities beyond the base role'}
              </div>
            </div>
            <span style={{ flexShrink: 0 }}><Pill tone="amber">{customRole.name}</Pill></span>
          </div>
          <div style={{ padding: '10px 12px', background: 'var(--nx-surface)', borderTop: '1px solid color-mix(in srgb, var(--nx-warning) 20%, transparent)' }}>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: deltaCaps.length > 0 ? 10 : 0, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
              <NexusIcon name="Info" size={11} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                {deltaCaps.length > 0
                  ? <>Custom roles <strong>add to</strong> the base role. Showing {deltaCaps.length} {deltaCaps.length === 1 ? 'capability' : 'capabilities'} that <strong>{customRole.name}</strong> grants beyond <strong>{ROLE_LABEL[baseRole] ?? baseRole}</strong>.</>
                  : <>Custom roles add to the base role — they do not replace it. All {customRolePerms.length} {customRolePerms.length === 1 ? 'permission' : 'permissions'} in <strong>{customRole.name}</strong> are already granted by <strong>{ROLE_LABEL[baseRole] ?? baseRole}</strong>.</>
                }
              </span>
            </div>
            {deltaCaps.length > 0 && (
              Object.entries(groupCapabilities(deltaCaps)).map(([pfx, caps]) => (
                <CapGroup key={pfx} prefix={pfx} caps={caps} source="custom" />
              ))
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: '1px dashed var(--nx-border)', fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <NexusIcon name="Tag" size={13} style={{ flexShrink: 0 }} />
          <span>No custom role assigned — all capabilities come from the base role above.</span>
        </div>
      )}

      {/* ── Workspace access ──────────────────────────────────────── */}
      {wsAssignments.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>
            Workspace Access ({wsAssignments.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {wsAssignments.map((ws) => (
              <WsCapRow key={ws.workspace?.id ?? ws.assignedAt} ws={ws} orgCaps={orgCaps} />
            ))}
          </div>
        </>
      )}

      {/* ── No workspace access ───────────────────────────────────── */}
      {wsAssignments.length === 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: '1px dashed var(--nx-border)', fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <NexusIcon name="LayoutGrid" size={13} style={{ flexShrink: 0 }} />
          <span>Not assigned to any workspaces — organization-level access only.</span>
        </div>
      )}

    </div>
  );
}

/* ── Overview tab helpers ────────────────────────────────────────────────── */

function OverviewRow({ icon, label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: '1px solid var(--nx-border)', fontSize: 13 }}>
      <NexusIcon name={icon} size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
      <span style={{ color: 'var(--nx-text-muted)', fontWeight: 500, minWidth: 120, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', fontWeight: 600, minWidth: 0, gap: 4 }}>{children}</div>
    </div>
  );
}

function OverviewSkeleton() {
  return <div className="nx-skeleton" style={{ height: 14, width: 80, borderRadius: 4 }} />;
}

function MemberDrawer({ orgId, member: initialMember, onClose, onUpdated, isSelf, wss }) {
  const toast                               = useToast();
  const navigate                            = useNavigate();
  const drawerPerm                          = usePermissions();
  const canManage                           = drawerPerm.flags.canManageMembers || drawerPerm.isSA;
  const [tab,          setTab]              = useState('overview');
  const [member,       setMember]           = useState(initialMember);
  const [detail,       setDetail]           = useState(null);
  const [detailErr,    setDetailErr]        = useState(null);
  const [memberships,  setMemberships]      = useState([]);
  const [newRole,               setNewRole]               = useState(initialMember.orgRole);
  const [rolesSaving,           setRolesSaving]           = useState(false);
  const [actionBusy,            setActionBusy]            = useState(false);
  const [confirm,               setConfirm]               = useState(null);
  const [pendingNavUrl,         setPendingNavUrl]         = useState(null);
  const [childModalOpen,        setChildModalOpen]        = useState(false);
  const [orgCustomRoles,        setOrgCustomRoles]        = useState([]);
  const [orgCustomRolesLoading, setOrgCustomRolesLoading] = useState(true);
  const [newCustomRoleId,       setNewCustomRoleId]       = useState('');
  const savingRef = useRef(false);

  useEffect(() => {
    setMember((m) => ({ ...m, status: initialMember.status, orgRole: initialMember.orgRole }));
  }, [initialMember.status, initialMember.orgRole]);

  useEffect(() => {
    let mounted = true;
    membersApi.getOne(orgId, member.id)
      .then((r) => { if (mounted) { setDetail(r); setMemberships(r.memberships ?? []); } })
      .catch((e) => { if (mounted) setDetailErr(e?.message ?? 'Failed to load member details'); });
    return () => { mounted = false; };
  }, [orgId, member.id]);

  const refetchDetail = useCallback(() => {
    membersApi.getOne(orgId, member.id)
      .then((r) => {
        setDetail(r);
        setMemberships(r.memberships ?? []);
        if (r.name) setMember((m) => ({ ...m, name: r.name }));
      })
      .catch(() => {});
  }, [orgId, member.id]);

  useEffect(() => {
    let mounted = true;
    orgRolesApi.list(orgId)
      .then((r) => { if (mounted) setOrgCustomRoles((r?.data ?? r ?? []).filter((role) => role.scope === 'org')); })
      .catch(() => {})
      .finally(() => { if (mounted) setOrgCustomRolesLoading(false); });
    return () => { mounted = false; };
  }, [orgId]);

  useEffect(() => {
    setNewCustomRoleId(detail?.orgCustomRole?.id ?? '');
  }, [detail?.orgCustomRole?.id]);

  const iamUrl      = `/organization/${orgId}/iam?tab=member-assignments&memberId=${member.id}`;
  const iamRolesUrl = `/organization/${orgId}/iam?tab=roles`;
  const anyRoleDirty = (newRole !== member.orgRole) || (newCustomRoleId !== (detail?.orgCustomRole?.id ?? ''));

  const applyRoles = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setRolesSaving(true);
    try {
      const patch = {};
      if (newRole !== member.orgRole) patch.orgRole = newRole;
      if (newCustomRoleId !== (detail?.orgCustomRole?.id ?? '')) patch.orgCustomRoleId = newCustomRoleId || null;
      if (Object.keys(patch).length === 0) return;
      const updated = await membersApi.update(orgId, member.id, patch);
      if (patch.orgRole) setMember((m) => ({ ...m, orgRole: updated.orgRole }));
      if ('orgCustomRoleId' in patch) {
        const roleName = newCustomRoleId ? (orgCustomRoles.find((r) => r.id === newCustomRoleId)?.name ?? '') : 'None';
        setDetail((d) => d ? { ...d, orgCustomRole: newCustomRoleId ? { id: newCustomRoleId, name: roleName, scope: 'org' } : null } : d);
      }
      toast({ tone: 'success', icon: 'Shield', title: 'Role changes saved' });
      onUpdated();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to save', desc: err?.message });
    } finally {
      savingRef.current = false;
      setRolesSaving(false);
    }
  };

  const navigateGuarded = useCallback((url) => {
    if (anyRoleDirty) { setPendingNavUrl(url); setConfirm('open-iam-dirty'); } else { navigate(url); }
  }, [anyRoleDirty, navigate]);

  const handleClose = useCallback(() => {
    if (anyRoleDirty) { setConfirm('close-dirty'); } else { onClose(); }
  }, [anyRoleDirty, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirm && !childModalOpen) handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirm, handleClose]);

  const lastLogin = detail?.user?.lastLoginAt;

  const runAction = async (action) => {
    setActionBusy(true);
    try {
      if (action === 'suspend')       { const r = await membersApi.suspend(orgId, member.id);      setMember((m) => ({ ...m, status: r.status })); }
      if (action === 'activate')      { const r = await membersApi.activate(orgId, member.id);     setMember((m) => ({ ...m, status: r.status })); }
      if (action === 'remove')        { await membersApi.remove(orgId, member.id); onClose(); }
      if (action === 'resetpw')       { await membersApi.sendPasswordReset(orgId, member.id); }
      if (action === 'resend-invite') {
        const r = await membersApi.resendInvite(orgId, member.id);
        if (r?.inviteToken) {
          toast({ tone: 'success', icon: 'Mail', title: 'New invite link generated', desc: `${window.location.origin}/invite/${r.inviteToken}` });
        } else {
          toast({ tone: 'success', icon: 'Mail', title: 'Invitation resent' });
        }
        onUpdated();
        setActionBusy(false);
        setConfirm(null);
        return;
      }
      if (action === 'cancel-invite') {
        await membersApi.cancelInvite(orgId, member.id);
        toast({ tone: 'success', icon: 'X', title: 'Invitation cancelled' });
        onUpdated();
        setActionBusy(false);
        setConfirm(null);
        onClose();
        return;
      }
      toast({ tone: 'success', icon: 'Check', title: action === 'resetpw' ? 'Reset link sent' : `Member ${action}d` });
      onUpdated();
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err?.message }); }
    finally { setActionBusy(false); setConfirm(null); }
  };

  const wsCount = memberships.filter((m) => m.workspace?.status === 'ACTIVE').length;

  return (
    <>
      <button type="button" aria-label="Close" tabIndex={-1} onClick={handleClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.28)', zIndex: 119, border: 'none', cursor: 'default', width: '100%', height: '100%' }} />
      <div role="dialog" aria-modal="true" aria-label={`Member: ${member.name}`} style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, background: 'var(--nx-bg)', borderLeft: '1px solid var(--nx-border)', zIndex: 120, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid var(--nx-border)', flexShrink: 0 }}>
          {/* Row 1: Avatar + identity + close */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div className="nx-domain-mark" style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', width: 40, height: 40, fontSize: 15, flexShrink: 0 }}>
              {initials(member.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {member.name && member.name !== member.email ? member.name : member.email.split('@')[0]}
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.email}</div>
            </div>
            <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 4, flexShrink: 0, borderRadius: 4 }} aria-label="Close drawer">
              <NexusIcon name="X" size={16} />
            </button>
          </div>
          {/* Row 2: Status + role pills + Open IAM */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 50 }}>
            <Pill tone={statTone(member.status)}>{statFmt(member.status)}</Pill>
            <Pill tone={roleTone(member.orgRole)}>{roleFmt(member.orgRole)}</Pill>
            <div style={{ flex: 1 }} />
            {canManage && (
              <Btn variant="ghost" size="sm" icon="ExternalLink" onClick={() => navigateGuarded(iamUrl)}>
                Open IAM
              </Btn>
            )}
          </div>
        </div>

        {/* Quick action bar */}
        {canManage && !isSelf && (
          <div style={{ padding: '8px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', gap: 0, flexWrap: 'wrap', alignItems: 'stretch', flexShrink: 0 }}>

            {/* Security group — password reset + session control */}
            {member.status !== 'PENDING' && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', paddingRight: 12, marginRight: 12, borderRight: '1px solid var(--nx-border)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 2 }}>Security</span>
                <Btn variant="ghost" size="sm" icon="Key" disabled={actionBusy} onClick={() => runAction('resetpw')} title="Send password reset email">Reset PW</Btn>
              </div>
            )}

            {/* Pending invite actions */}
            {member.status === 'PENDING' && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', paddingRight: 12, marginRight: 12, borderRight: '1px solid var(--nx-border)' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 2 }}>Invite</span>
                <Btn variant="secondary" size="sm" icon="Mail" disabled={actionBusy} onClick={() => runAction('resend-invite')}>Resend</Btn>
                <Btn variant="ghost"     size="sm" icon="X"    disabled={actionBusy} onClick={() => setConfirm('cancel-invite')}>Cancel Invite</Btn>
              </div>
            )}

            {/* Status control */}
            {member.status !== 'PENDING' && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', paddingRight: 12, marginRight: 12, borderRight: '1px solid var(--nx-border)' }}>
                {member.status === 'ACTIVE'
                  ? <Btn variant="ghost" size="sm" icon="PauseCircle" disabled={actionBusy} onClick={() => setConfirm('suspend')}>Suspend</Btn>
                  : <Btn variant="ghost" size="sm" icon="Play"        disabled={actionBusy} onClick={() => runAction('activate')}>Activate</Btn>
                }
              </div>
            )}

            {/* Danger zone */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
              <Btn variant="danger" size="sm" icon="Trash" disabled={actionBusy} onClick={() => setConfirm('remove')}>Remove</Btn>
            </div>
          </div>
        )}

        <TabBar tabs={DRAWER_TABS} active={tab} onChange={setTab} />

        {tab === 'profile' ? (
          <ProfileTab orgId={orgId} member={member} detail={detail} detailLoading={!detail && !detailErr} detailErr={detailErr} onUpdated={() => { refetchDetail(); onUpdated(); }} />
        ) : (
        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {detailErr && (
            <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, background: 'rgba(220,38,38,0.06)', border: '1px solid var(--nx-danger)', color: 'var(--nx-danger)', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <NexusIcon name="AlertCircle" size={13} />
              <span>Could not load member details — some information may be unavailable.</span>
            </div>
          )}
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Status callout — context-aware banner for non-active states */}
              {member.status === 'PENDING' && (
                <div role="status" style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', fontSize: 13 }}>
                  <NexusIcon name="Mail" size={15} style={{ color: 'var(--nx-warning)', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Awaiting invitation acceptance</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>Invite sent {fmtDate(member.invitedAt)}. Member has not accepted yet.</div>
                  </div>
                </div>
              )}
              {member.status === 'SUSPENDED' && (
                <div role="status" style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.30)', fontSize: 13 }}>
                  <NexusIcon name="PauseCircle" size={15} style={{ color: 'var(--nx-danger)', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Account suspended</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>This member cannot log in or access any workspace.</div>
                  </div>
                </div>
              )}
              {member.status === 'EXPIRED' && (
                <div role="status" style={{ display: 'flex', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', fontSize: 13 }}>
                  <NexusIcon name="Clock" size={15} style={{ color: 'var(--nx-text-muted)', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Invitation expired</div>
                    <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>The invite link has expired. Resend to give them access.</div>
                  </div>
                </div>
              )}

              {/* Role & Access */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Role &amp; Access</div>
                <OverviewRow icon="Shield" label="Org Role">
                  <Pill tone={roleTone(member.orgRole)}>{roleFmt(member.orgRole)}</Pill>
                </OverviewRow>
                {(!detail && !detailErr) && (
                  <OverviewRow icon="Tag" label="Custom Role"><OverviewSkeleton /></OverviewRow>
                )}
                {detail?.orgCustomRole && (
                  <OverviewRow icon="Tag" label="Custom Role">
                    <Pill tone="purple">{detail.orgCustomRole.name}</Pill>
                  </OverviewRow>
                )}
                <OverviewRow icon="LayoutGrid" label="Workspaces">
                  {(!detail && !detailErr)
                    ? <OverviewSkeleton />
                    : wsCount === 0
                      ? <span style={{ fontSize: 13, color: 'var(--nx-text-muted)', fontWeight: 400 }}>No access</span>
                      : (
                        <>
                          {memberships.filter((m) => m.workspace?.status === 'ACTIVE').slice(0, 3).map((m) => (
                            <Pill key={m.id} tone="slate">{m.workspace.name}</Pill>
                          ))}
                          {wsCount > 3 && <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', fontWeight: 400 }}>+{wsCount - 3} more</span>}
                        </>
                      )
                  }
                </OverviewRow>
              </div>

              {/* Timeline */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Timeline</div>
                {member.invitedAt && <OverviewRow icon="UserPlus" label="Invited">{fmtDate(member.invitedAt)}</OverviewRow>}
                {member.joinedAt  && <OverviewRow icon="CheckCircle" label="Joined">{fmtDate(member.joinedAt)}</OverviewRow>}
                <OverviewRow icon="LogIn" label="Last Login">
                  {(!detail && !detailErr)
                    ? <OverviewSkeleton />
                    : lastLogin
                      ? new Date(lastLogin).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : <span style={{ color: 'var(--nx-text-muted)', fontWeight: 400 }}>Never</span>
                  }
                </OverviewRow>
              </div>

              {/* Profile — only rendered when at least one field has a value */}
              {detail && (detail.user?.department || detail.user?.phone || detail.user?.timezone) && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Profile</div>
                  {detail.user?.department && <OverviewRow icon="Briefcase" label="Department">{detail.user.department}</OverviewRow>}
                  {detail.user?.phone      && <OverviewRow icon="Phone"     label="Phone">{detail.user.phone}</OverviewRow>}
                  {detail.user?.timezone   && <OverviewRow icon="Globe"     label="Timezone">{detail.user.timezone}</OverviewRow>}
                </div>
              )}

              {/* SA: member ID for debugging */}
              {drawerPerm.isSA && (
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'var(--nx-surface-alt)', fontSize: 11, color: 'var(--nx-text-muted)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  Member ID: {member.id}
                </div>
              )}
            </div>
          )}

          {tab === 'sessions' && (
            <SessionsTab
              orgId={orgId}
              memberId={member.id}
              memberStatus={member.status}
              isSelf={isSelf}
              canManage={canManage && !isSelf}
              onModalChange={setChildModalOpen}
            />
          )}

          {tab === 'audit' && (
            <AuditTab orgId={orgId} member={member} />
          )}

          {tab === 'workspace-summary' && (
            <WorkspaceSummaryTab
              orgId={orgId}
              memberId={member.id}
              memberStatus={member.status}
              wss={wss}
              memberships={memberships}
              loading={!detail && !detailErr}
              canManage={canManage && !isSelf}
              onRefresh={() => { refetchDetail(); onUpdated(); }}
              onModalChange={setChildModalOpen}
            />
          )}

          {tab === 'role-summary' && (
            <RoleSummaryTab
              orgId={orgId}
              member={member}
              detail={detail}
              memberships={memberships}
              detailLoading={!detail && !detailErr}
              detailErr={detailErr}
              canManage={canManage && !isSelf}
              orgCustomRoles={orgCustomRoles}
              orgCustomRolesLoading={orgCustomRolesLoading}
              newRole={newRole}
              onRoleChange={setNewRole}
              newCustomRoleId={newCustomRoleId}
              onCustomRoleChange={setNewCustomRoleId}
              onApplyRoles={applyRoles}
              rolesSaving={rolesSaving}
              onOpenIAM={() => navigateGuarded(iamUrl)}
              onNavigateIAMRoles={() => navigateGuarded(iamRolesUrl)}
              onResendInvite={member.status === 'PENDING' ? () => runAction('resend-invite') : undefined}
              resendBusy={actionBusy}
            />
          )}

          {tab === 'permission-summary' && (
            <PermissionSummaryTab orgId={orgId} memberId={member.id} />
          )}
        </div>
        )}
      </div>

      {/* Confirm dialogs */}
      {confirm === 'suspend' && (
        <ConfirmDialog
          title="Suspend Member"
          desc={`Suspend ${member.name}? They will lose access immediately.`}
          confirmLabel="Suspend"
          danger
          busy={actionBusy}
          onConfirm={() => runAction('suspend')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'remove' && (
        <ConfirmDialog
          title="Remove Member"
          desc={`Remove ${member.name} from this organization? This cannot be undone.`}
          confirmLabel="Remove"
          danger
          busy={actionBusy}
          onConfirm={() => runAction('remove')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'cancel-invite' && (
        <ConfirmDialog
          title="Cancel Invitation"
          desc={`Cancel the pending invitation for ${member.name} (${member.email})?`}
          confirmLabel="Cancel Invite"
          danger
          busy={actionBusy}
          onConfirm={() => runAction('cancel-invite')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'open-iam-dirty' && (
        <ConfirmDialog
          title="Discard unsaved role changes?"
          desc="You have role changes that haven't been applied yet. If you leave now, they will be discarded."
          confirmLabel="Leave without applying"
          danger={false}
          busy={false}
          onConfirm={() => { setConfirm(null); navigate(pendingNavUrl ?? iamUrl); setPendingNavUrl(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'close-dirty' && (
        <ConfirmDialog
          title="Discard unsaved role changes?"
          desc="You have role changes that haven't been applied yet. If you close now, they will be discarded."
          confirmLabel="Discard and close"
          danger={false}
          busy={false}
          onConfirm={() => { setConfirm(null); onClose(); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

/* ── InviteModal — 3-step wizard ─────────────────────────────────────────── */

function InviteModal({ orgId, orgName, limits, usageUsers, wss, onClose, onInvited }) {
  const toast                              = useToast();
  const [step,           setStep]          = useState(1);
  const [form,           setForm]          = useState({ name: '', email: '', orgRole: 'MEMBER', customRoleId: '' });
  const [wsAssign,       setWsAssign]      = useState([]);
  const [busy,           setBusy]          = useState(false);
  const [inviteUrl,      setInviteUrl]     = useState(null);
  const [invOrgRoles,    setInvOrgRoles]   = useState([]);

  const maxSeats  = limits.maxUsers ?? 0;
  const usedSeats = usageUsers ?? 0;
  const seatsFull = maxSeats > 0 && usedSeats >= maxSeats;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleWs = (wsId) => setWsAssign((prev) => {
    const exists = prev.find((x) => x.wsId === wsId);
    if (exists) return prev.filter((x) => x.wsId !== wsId);
    return [...prev, { wsId, wsRole: 'WORKSPACE_USER' }];
  });

  const setWsRole = (wsId, wsRole) => setWsAssign((prev) =>
    prev.map((x) => x.wsId === wsId ? { ...x, wsRole } : x),
  );

  const step1Valid = /.+@.+\..+/.test(form.email);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy && !inviteUrl) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, inviteUrl, onClose]);

  useEffect(() => {
    let mounted = true;
    orgRolesApi.list(orgId)
      .then((r) => { if (mounted) setInvOrgRoles((r?.data ?? r ?? []).filter((role) => role.scope === 'org')); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [orgId]);

  const handleSend = async () => {
    setBusy(true);
    try {
      const res = await membersApi.invite(orgId, {
        email:          form.email.trim(),
        name:           form.name.trim() || form.email.split('@')[0],
        orgRole:        form.orgRole,
        ...(form.customRoleId ? { orgCustomRoleId: form.customRoleId } : {}),
      });
      const memberId = res?.id;
      if (wsAssign.length > 0 && memberId) {
        // MEDIUM-13: surface workspace assignment failures instead of silently dropping them
        const wsResults = await Promise.allSettled(
          wsAssign.map(({ wsId, wsRole }) => workspaceApi.addMember(wsId, { memberId, wsRole })),
        );
        const wsFailed = wsResults.filter((r) => r.status === 'rejected').length;
        if (wsFailed > 0) {
          toast({ tone: 'warning', icon: 'AlertCircle', title: `${wsFailed} workspace assignment${wsFailed !== 1 ? 's' : ''} failed`, desc: 'Member invited. Some workspace assignments could not be completed.' });
        }
      }
      if (res?.inviteToken) {
        setInviteUrl(`${window.location.origin}/invite/${res.inviteToken}`);
      } else {
        toast({ tone: 'success', icon: 'Mail', title: 'Invitation sent', desc: form.email });
        onInvited();
        onClose();
      }
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Invite failed', desc: err?.message }); }
    finally { setBusy(false); }
  };

  const STEPS = ['Details', 'Workspaces', 'Review'];

  return (
    <div className="nx-modal-veil">
      <button type="button" aria-label="Close dialog" tabIndex={-1} onClick={onClose}
        style={{ position: 'absolute', inset: 0, border: 'none', cursor: 'default', background: 'transparent', width: '100%', height: '100%' }} />
      <div role="dialog" aria-modal="true" aria-label="Invite Member" style={{ position: 'relative', zIndex: 1, background: 'var(--nx-bg)', borderRadius: 10, padding: 24, width: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexShrink: 0 }}>
          <NexusIcon name="UserPlus" size={18} style={{ color: 'var(--nx-accent)' }} />
          <span style={{ fontWeight: 700, fontSize: 15 }}>Invite Member — {orgName}</span>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-text-muted)', padding: 2 }}><NexusIcon name="X" size={14} /></button>
        </div>

        {/* Step indicator */}
        {!inviteUrl && (
          <div style={{ display: 'flex', gap: 0, marginBottom: 20, flexShrink: 0 }}>
            {STEPS.map((label, i) => {
              const n = i + 1;
              const active = n === step;
              const done   = n < step;
              return (
                <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, background: stepDotBg(done, active), color: done || active ? '#fff' : 'var(--nx-text-muted)' }}>
                    {done ? <NexusIcon name="Check" size={11} /> : n}
                  </div>
                  <span style={{ fontSize: 10, color: active ? 'var(--nx-accent)' : 'var(--nx-text-muted)', fontWeight: active ? 700 : 400 }}>{label}</span>
                </div>
              );
            })}
          </div>
        )}

        {seatsFull && step === 1 && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 6, background: 'rgba(220,38,38,0.08)', border: '1px solid var(--nx-danger)', color: 'var(--nx-danger)', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            <NexusIcon name="AlertOctagon" size={13} />
            <span>Seat limit reached ({usedSeats}/{maxSeats}). Upgrade your plan to invite more members.</span>
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto' }}>

          {/* Step 1 — Details */}
          {step === 1 && (
            <div className="nx-form" style={{ gap: 10 }}>
              <div className="nx-form-row">
                <label htmlFor="inv-name">Full Name</label>
                <input id="inv-name" className="nx-input" autoFocus placeholder="Full name" value={form.name} onChange={(e) => set('name', e.target.value)} />
              </div>
              <div className="nx-form-row">
                <label htmlFor="inv-email">Email *</label>
                <input id="inv-email" className="nx-input" type="email" placeholder="name@company.com" value={form.email} onChange={(e) => set('email', e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && step1Valid) setStep(2); }} />
              </div>
              <div className="nx-form-row">
                <label htmlFor="inv-role">Org Role</label>
                <select id="inv-role" className="nx-input" value={form.orgRole} onChange={(e) => set('orgRole', e.target.value)}>
                  {ROLE_OPTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {invOrgRoles.length > 0 && (
                <div className="nx-form-row">
                  <label htmlFor="inv-custom-role">Custom Role <span style={{ fontWeight: 400, color: 'var(--nx-text-muted)' }}>(optional)</span></label>
                  <select id="inv-custom-role" className="nx-input" value={form.customRoleId} onChange={(e) => set('customRoleId', e.target.value)}>
                    <option value="">No custom role</option>
                    {invOrgRoles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              )}
              {maxSeats > 0 && (
                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Seats: {usedSeats} used / {maxSeats} total</div>
              )}
            </div>
          )}

          {/* Step 2 — Workspace Access */}
          {step === 2 && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 12 }}>
                Optionally assign {form.name || form.email} to workspaces now. You can also do this later in Identity &amp; Access.
              </p>
              {wss.length === 0
                ? <div className="nx-empty"><NexusIcon name="LayoutGrid" size={18} /><span>No workspaces in this organization.</span></div>
                : wss.map((ws) => {
                  const assigned = wsAssign.find((x) => x.wsId === ws.id);
                  return (
                    <div key={ws.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--nx-border)' }}>
                      <input type="checkbox" id={`ws-${ws.id}`} checked={Boolean(assigned)} onChange={() => toggleWs(ws.id)} style={{ cursor: 'pointer', flexShrink: 0 }} />
                      <label htmlFor={`ws-${ws.id}`} style={{ flex: 1, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>{ws.name}</label>
                      {assigned && (
                        <select className="nx-input" style={{ width: 130, fontSize: 12 }} value={assigned.wsRole} onChange={(e) => setWsRole(ws.id, e.target.value)}>
                          <option value="WORKSPACE_USER">WS User</option>
                          <option value="WORKSPACE_ADMIN">WS Admin</option>
                        </select>
                      )}
                    </div>
                  );
                })
              }
            </div>
          )}

          {/* Step 3 — Review */}
          {step === 3 && (
            <div>
              <div style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Invite Summary</div>
                {[
                  ['Name', form.name || '(auto from email)'],
                  ['Email', form.email],
                  ['Org Role', ROLE_LABEL[form.orgRole]],
                  ...(form.customRoleId ? [['Custom Role', invOrgRoles.find((r) => r.id === form.customRoleId)?.name ?? form.customRoleId]] : []),
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: 8, fontSize: 13, marginBottom: 4 }}>
                    <span style={{ color: 'var(--nx-text-muted)', minWidth: 80 }}>{k}</span>
                    <span style={{ fontWeight: 500 }}>{v}</span>
                  </div>
                ))}
              </div>
              {wsAssign.length > 0 && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Workspace Access ({wsAssign.length})</div>
                  {wsAssign.map(({ wsId, wsRole }) => {
                    const ws = wss.find((w) => w.id === wsId);
                    return (
                      <div key={wsId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <span>{ws?.name ?? wsId}</span>
                        <Pill tone={wsRole === 'WORKSPACE_ADMIN' ? 'purple' : 'slate'}>{WS_ROLE_LABEL[wsRole] ?? wsRole}</Pill>
                      </div>
                    );
                  })}
                </div>
              )}
              {wsAssign.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>No workspace access assigned — can be configured later in Identity &amp; Access.</p>
              )}
            </div>
          )}

          {/* Done — invite link */}
          {inviteUrl && (
            <div>
              <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 12 }}>Member invited. Share this link so they can set their password.</p>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <input className="nx-input" readOnly value={inviteUrl} style={{ flex: 1, fontSize: 11, fontFamily: 'monospace' }} onClick={(e) => e.target.select()} />
                <Btn variant="ghost" size="sm" icon="Copy" onClick={() => { navigator.clipboard.writeText(inviteUrl); toast({ tone: 'success', icon: 'Copy', title: 'Link copied' }); }}>Copy</Btn>
              </div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 16 }}>Valid for 7 days. Share only with the invitee.</div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16, flexShrink: 0, borderTop: '1px solid var(--nx-border)', paddingTop: 14 }}>
          {inviteUrl
            ? <Btn variant="primary" onClick={() => { onInvited(); onClose(); }}>Done</Btn>
            : (
              <>
                <Btn variant="ghost" icon={step > 1 ? 'ChevronLeft' : undefined} onClick={step > 1 ? () => setStep((s) => s - 1) : onClose}>
                  {step > 1 ? 'Back' : 'Cancel'}
                </Btn>
                <div style={{ display: 'flex', gap: 8 }}>
                  {step === 2 && <Btn variant="ghost" onClick={() => setStep(3)}>Skip</Btn>}
                  {step < 3
                    ? <Btn variant="primary" icon="ChevronRight" disabled={step === 1 && (seatsFull || !step1Valid)} onClick={() => setStep((s) => s + 1)}>Next</Btn>
                    : <Btn variant="primary" icon="Mail" disabled={busy} onClick={handleSend}>{busy ? 'Sending…' : 'Send Invite'}</Btn>
                  }
                </div>
              </>
            )
          }
        </div>
      </div>
    </div>
  );
}

/* ── ImportMembersModal ──────────────────────────────────────────────────── */

const CSV_PLACEHOLDER = `alice@example.com, Alice Smith, MEMBER
bob@example.com, Bob Jones
carol@example.com`;

// LOW-08: RFC-4180 compliant CSV tokenizer — handles quoted fields with embedded commas
function parseCSVFields(line) {
  const fields = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { field += '"'; i++; }  // escaped quote
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(field.trim());
      field = '';
    } else {
      field += ch;
    }
  }
  fields.push(field.trim());
  return fields;
}

function parseImportText(raw) {
  const VALID_ROLES = ['MEMBER', 'VIEWER', 'ORG_ADMIN'];
  const seen = new Set();  // LOW-10: dedup by email
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [email = '', name = '', orgRole = ''] = parseCSVFields(line);  // LOW-08
      const inputRole = orgRole.toUpperCase();
      const validRole  = VALID_ROLES.includes(inputRole) ? inputRole : 'MEMBER';
      // LOW-17: flag rows where the original role was non-empty but invalid
      const roleInvalid = Boolean(orgRole.trim()) && !VALID_ROLES.includes(inputRole);
      return { email: email.toLowerCase(), name: name || email.split('@')[0], orgRole: validRole, roleInvalid };
    })
    .filter((r) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email))
    .filter((r) => {                         // LOW-10: first occurrence wins
      if (seen.has(r.email)) return false;
      seen.add(r.email);
      return true;
    });
}

function ImportMembersModal({ orgId, onClose, onImported }) {
  const toast = useToast();
  const [raw,      setRaw]      = useState('');
  const [rows,     setRows]     = useState([]);
  const [busy,     setBusy]     = useState(false);
  const [result,   setResult]   = useState(null);  /* { invited, failed } */

  const preview = () => setRows(parseImportText(raw));

  const submit = async () => {
    if (rows.length === 0) return;
    setBusy(true);
    try {
      // LOW-17: strip UI-only flag before sending to API
      const payload = rows.map(({ roleInvalid: _ri, ...rest }) => rest);
      const res = await membersApi.batchInvite(orgId, payload);
      setResult(res);
      if (res.invited > 0) onImported();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Import failed', desc: err?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="nx-modal-backdrop" onClick={onClose}>
      <div className="nx-modal" style={{ width: 560, maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="nx-modal-header">
          <span className="nx-modal-title">Import Members</span>
          <button className="nx-modal-close" onClick={onClose} aria-label="Close"><NexusIcon name="X" size={16} /></button>
        </div>

        {!result ? (
          <>
            <div style={{ padding: '0 20px 8px', fontSize: 13, color: 'var(--nx-text-muted)' }}>
              Paste one member per line: <code>email, name, role</code><br />
              Name and role are optional (defaults: email prefix, MEMBER).
            </div>
            <div style={{ padding: '0 20px 12px' }}>
              <textarea
                style={{ width: '100%', minHeight: 140, fontFamily: 'monospace', fontSize: 12, padding: 10, borderRadius: 6, border: '1px solid var(--nx-border)', background: 'var(--nx-surface-alt)', color: 'var(--nx-text)', resize: 'vertical', boxSizing: 'border-box' }}
                placeholder={CSV_PLACEHOLDER}
                value={raw}
                onChange={(e) => { setRaw(e.target.value); setRows([]); }}
              />
            </div>

            {rows.length === 0 && raw.trim() && (
              <div style={{ padding: '0 20px 12px' }}>
                <Btn variant="secondary" size="sm" icon="Eye" onClick={preview}>Preview ({parseImportText(raw).length} valid rows)</Btn>
              </div>
            )}

            {rows.length > 0 && (
              <div style={{ padding: '0 20px 12px', overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: 'var(--nx-text-muted)', borderBottom: '1px solid var(--nx-border)' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--nx-border-subtle, var(--nx-border))' }}>
                        <td style={{ padding: '5px 6px' }}>{r.email}</td>
                        <td style={{ padding: '5px 6px' }}>{r.name}</td>
                        <td style={{ padding: '5px 6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <select
                              value={r.orgRole}
                              style={{ fontSize: 12, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 4, padding: '2px 4px', color: 'var(--nx-text)' }}
                              onChange={(e) => setRows((prev) => prev.map((x, j) => j === i ? { ...x, orgRole: e.target.value, roleInvalid: false } : x))}
                            >
                              {ROLE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            {/* LOW-17: warn when the original CSV role was invalid; defaulted to MEMBER */}
                            {r.roleInvalid && (
                              <span title="Original role value was invalid — defaulted to Member" style={{ fontSize: 11, padding: '1px 5px', borderRadius: 3, background: 'var(--nx-warning-subtle, #fef3c7)', color: 'var(--nx-warning, #92400e)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                invalid role
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="nx-modal-footer">
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
              {rows.length === 0
                ? <Btn variant="primary" disabled={!raw.trim()} onClick={preview}>Preview</Btn>
                : <Btn variant="primary" icon="UserPlus" disabled={busy} onClick={submit}>{busy ? 'Importing…' : `Invite ${rows.length} member${rows.length !== 1 ? 's' : ''}`}</Btn>
              }
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '20px 20px 8px' }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
                {result.invited} invitation{result.invited !== 1 ? 's' : ''} sent
              </div>
              {result.failed.length > 0 && (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 6 }}>{result.failed.length} failed:</div>
                  {result.failed.map((f) => (
                    <div key={f.email} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--nx-border-subtle, var(--nx-border))' }}>
                      <span style={{ fontWeight: 600 }}>{f.email}</span>
                      <span style={{ color: 'var(--nx-text-muted)', marginLeft: 8 }}>{f.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="nx-modal-footer">
              <Btn variant="primary" onClick={onClose}>Done</Btn>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Main OrgMembers ─────────────────────────────────────────────────────── */

export default function OrgMembers() {
  const { orgId }    = useParams();
  const toast        = useToast();
  const org          = useOrganization();
  const limits       = useLimits();
  const usage        = useUsage();
  const currentUser  = useCurrentOrgUser();
  const { invalidateRuntime } = useOrgRuntime();

  const perm      = usePermissions();
  // MEDIUM-18: granular capability checks instead of single canManageMembers flag
  const canInvite = perm.has(ORG.MEMBERS_INVITE) || perm.isSA;
  const canEdit   = perm.has(ORG.MEMBERS_EDIT)   || perm.isSA;
  const canRemove = perm.has(ORG.MEMBERS_REMOVE)  || perm.isSA;
  const canManage = canInvite || canEdit || canRemove;
  const maxSeats  = limits.maxUsers ?? 0;
  const usedSeats = usage.users ?? 0;
  const seatsFull = maxSeats > 0 && usedSeats >= maxSeats;
  const seatPctVal = seatPct(usedSeats, maxSeats);

  const [wss, setWss] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    orgApi.listWorkspaces(orgId, { pageSize: 200 })
      .then((r) => setWss(r.data ?? []))
      .catch(() => {});
  }, [orgId]);

  const { members, pag, loading, error, q, statusF, roleF, stats, sortField, sortDir, search, filterStatus, filterRole, clearFilters, goPage, sortBy, refresh } = useMembers(orgId);

  const [selected,        setSelected]        = useState(() => new Set());
  const [drawerMember,    setDrawerMember]    = useState(null);
  const [showInvite,      setShowInvite]      = useState(false);
  const [showImport,      setShowImport]      = useState(false);
  const [bulkBusy,        setBulkBusy]        = useState(false);
  const [bulkConfirm,     setBulkConfirm]     = useState(null);   /* { action, targets } */
  const [listConfirm,     setListConfirm]     = useState(null);   /* { action, member } */
  const [listConfirmBusy, setListConfirmBusy] = useState(false);

  const toggleSelect = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) { next.delete(id); } else { next.add(id); }
    return next;
  });

  const toggleSelectAll = (rows) => {
    const manageable = rows.filter((m) => m.userId !== currentUser?.id);
    const allChecked = manageable.length > 0 && manageable.every((m) => selected.has(m.id));
    if (allChecked) {
      setSelected(new Set());
    } else {
      setSelected(new Set(manageable.map((m) => m.id)));
    }
  };

  const invalidate = useCallback(() => invalidateRuntime(orgId), [invalidateRuntime, orgId]);

  const doAction = async (action, member) => {
    setListConfirmBusy(true);
    try {
      let updated;
      if (action === 'activate')      { updated = await membersApi.activate(orgId, member.id); }
      if (action === 'suspend')       { updated = await membersApi.suspend(orgId, member.id);  }
      if (action === 'remove')        { await membersApi.remove(orgId, member.id); }
      if (action === 'resend-invite') { await membersApi.resendInvite(orgId, member.id); }
      if (action === 'cancel-invite') { await membersApi.cancelInvite(orgId, member.id); }
      invalidate();
      refresh();
      if (drawerMember?.id === member.id) {
        if (action === 'remove') {
          setDrawerMember(null);
        } else if (action === 'cancel-invite') {
          setDrawerMember(null);
        } else if (updated?.status) {
          setDrawerMember((d) => d ? { ...d, status: updated.status } : d);
        }
      }
      setListConfirm(null);
      let label;
      if (action === 'resend-invite')  label = 'Invitation resent';
      else if (action === 'cancel-invite') label = 'Invitation cancelled';
      else label = `Member ${action}d`;
      toast({ tone: 'success', icon: 'Check', title: label });
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err?.message });
    } finally {
      setListConfirmBusy(false);
    }
  };

  const handleAction = (action, member) => {
    const needsConfirm = action === 'remove' || action === 'suspend' || action === 'cancel-invite';
    if (needsConfirm) { setListConfirm({ action, member }); } else { doAction(action, member); }
  };

  // MEDIUM-10: extracted execution so confirm modal can invoke it
  const executeBulk = async (action, targets) => {
    setBulkConfirm(null);
    const ids = targets.map((m) => m.id);
    setBulkBusy(true);
    const results = await Promise.allSettled(
      ids.map((id) => {
        if (action === 'activate')      return membersApi.activate(orgId, id);
        if (action === 'suspend')       return membersApi.suspend(orgId, id);
        if (action === 'resend-invite') return membersApi.resendInvite(orgId, id);
        if (action === 'cancel-invite') return membersApi.cancelInvite(orgId, id);
        return membersApi.remove(orgId, id);
      }),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    const verb = action === 'resend-invite' ? 'resent'
               : action === 'cancel-invite' ? 'cancelled'
               : action + 'd';
    if (failed > 0) toast({ tone: 'warning', icon: 'AlertCircle', title: `${failed} of ${ids.length} failed` });
    else toast({ tone: 'success', icon: 'Check', title: `${ids.length} member${ids.length !== 1 ? 's' : ''} ${verb}` });
    invalidate();
    refresh();
    setSelected(new Set());
    setBulkBusy(false);
  };

  const doBulk = (action) => {
    const selRows = members.filter((m) => selected.has(m.id));
    const targets = action === 'resend-invite' || action === 'cancel-invite'
      ? selRows.filter((m) => m.status === 'PENDING')
      : selRows;
    if (targets.length === 0) return;
    // MEDIUM-10: require confirmation before destructive bulk actions
    if (action === 'suspend' || action === 'remove') {
      setBulkConfirm({ action, targets });
      return;
    }
    executeBulk(action, targets);
  };

  if (!org) return null;

  const hasFilters = Boolean(q || statusF || roleF);

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Members"
        sub={`${pag.total} member${pag.total !== 1 ? 's' : ''} · ${org.name}`}
        actions={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Btn variant="ghost" size="sm" icon="Download" onClick={() => {
              membersApi.list(orgId, { pageSize: 500 }).then((r) => {
                // DEFECT-V03: RFC-4180 quoting — escape fields containing comma, quote, or newline
                const csvQ = (v) => { const s = String(v ?? ''); return /[,"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
                const rows = (r.data ?? []).map((m) => [m.name, m.email, m.orgRole, m.status, fmtDate(m.joinedAt), fmtDate(m.user?.lastLoginAt)].map(csvQ).join(','));
                const csv  = ['Name,Email,Role,Status,Joined,Last Login', ...rows].join('\n');
                const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                const a    = document.createElement('a'); a.href = url; a.download = 'members.csv'; a.click(); URL.revokeObjectURL(url);
              });
            }}>Export</Btn>
            {canInvite && (
              <>
                <Btn variant="secondary" icon="Upload" disabled={seatsFull} onClick={() => setShowImport(true)}>Import</Btn>
                <Btn variant="primary"   icon="UserPlus" disabled={seatsFull} onClick={() => setShowInvite(true)}>Invite Member</Btn>
              </>
            )}
          </div>
        }
      />

      {maxSeats > 0 && seatPctVal >= 80 && (
        <SeatBanner used={usedSeats} max={maxSeats} />
      )}

      <SeatSummaryStrip stats={stats} limits={limits} usageUsers={usedSeats} />

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={q} onChange={(e) => search(e.target.value)} placeholder="Search name or email…" width={280} />
        <select aria-label="Filter by status" className="nx-input" style={{ width: 160, fontSize: 13 }} value={statusF} onChange={(e) => filterStatus(e.target.value)}>
          {STATUS_FILTER_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select aria-label="Filter by role" className="nx-input" style={{ width: 140, fontSize: 13 }} value={roleF} onChange={(e) => filterRole(e.target.value)}>
          {ROLE_FILTER_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {hasFilters && (
          <Btn variant="ghost" size="sm" icon="X" onClick={clearFilters}>Clear filters</Btn>
        )}
        <div style={{ flex: 1 }} />
        {selected.size === 0 && (
          <Btn variant="ghost" size="sm" icon="RefreshCw" onClick={refresh}>Refresh</Btn>
        )}
      </div>

      {members.length === 0 && !loading && !hasFilters ? (
        <div className="nx-empty">
          <NexusIcon name="Users" size={28} />
          <span>No members yet in {org.name}.</span>
          {canInvite && !seatsFull && (
            <Btn variant="secondary" icon="UserPlus" onClick={() => setShowInvite(true)}>Invite first member</Btn>
          )}
        </div>
      ) : (
        <MembersTable
          members={members}
          loading={loading}
          error={error}
          pag={pag}
          selected={selected}
          onSelect={toggleSelect}
          onSelectAll={toggleSelectAll}
          onOpen={setDrawerMember}
          onAction={handleAction}
          currentUserId={currentUser?.id}
          goPage={goPage}
          orgId={orgId}
          statusF={statusF}
          sortField={sortField}
          sortDir={sortDir}
          onSort={sortBy}
        />
      )}

      {selected.size > 0 && (
        <BulkBar
          selected={selected}
          members={members}
          onClear={() => setSelected(new Set())}
          onBulkAction={doBulk}
          busy={bulkBusy}
        />
      )}

      {drawerMember && (
        <MemberDrawer
          key={drawerMember.id}
          orgId={orgId}
          member={drawerMember}
          wss={wss}
          isSelf={drawerMember.userId === currentUser?.id}
          onClose={() => setDrawerMember(null)}
          onUpdated={() => { invalidate(); refresh(); }}
        />
      )}

      {showImport && (
        <ImportMembersModal
          orgId={orgId}
          onClose={() => setShowImport(false)}
          onImported={() => { invalidate(); refresh(); setShowImport(false); }}
        />
      )}

      {showInvite && (
        <InviteModal
          orgId={orgId}
          orgName={org.name}
          limits={limits}
          usageUsers={usedSeats}
          wss={wss.filter((w) => w.status === 'ACTIVE')}
          onClose={() => setShowInvite(false)}
          onInvited={() => { invalidate(); refresh(); }}
        />
      )}

      {listConfirm && (() => {
        const lca = listConfirm.action;
        let confirmTitle, confirmDesc;
        if (lca === 'remove')        { confirmTitle = 'Remove Member';      confirmDesc = `Remove ${listConfirm.member.name} from ${org.name}? This cannot be undone.`; }
        else if (lca === 'cancel-invite') { confirmTitle = 'Cancel Invitation'; confirmDesc = `Cancel the pending invitation for ${listConfirm.member.name}?`; }
        else                         { confirmTitle = 'Suspend Member';     confirmDesc = `Suspend ${listConfirm.member.name}? They will lose access immediately.`; }
        const lclLabel = lca === 'remove' ? 'Remove' : lca === 'cancel-invite' ? 'Cancel Invite' : 'Suspend';
        return (
        <ConfirmDialog
          title={confirmTitle}
          desc={confirmDesc}
          confirmLabel={lclLabel}
          danger
          busy={listConfirmBusy}
          onConfirm={() => doAction(listConfirm.action, listConfirm.member)}
          onCancel={() => setListConfirm(null)}
        />
        );
      })()}

      {/* MEDIUM-10: confirm before destructive bulk actions */}
      {bulkConfirm && (
        <ConfirmDialog
          title={bulkConfirm.action === 'remove'
            ? `Remove ${bulkConfirm.targets.length} Member${bulkConfirm.targets.length !== 1 ? 's' : ''}`
            : `Suspend ${bulkConfirm.targets.length} Member${bulkConfirm.targets.length !== 1 ? 's' : ''}`}
          desc={bulkConfirm.action === 'remove'
            ? `Remove ${bulkConfirm.targets.length} selected member${bulkConfirm.targets.length !== 1 ? 's' : ''} from ${org.name}? This cannot be undone.`
            : `Suspend ${bulkConfirm.targets.length} selected member${bulkConfirm.targets.length !== 1 ? 's' : ''}? They will lose access immediately.`}
          confirmLabel={bulkConfirm.action === 'remove' ? 'Remove' : 'Suspend'}
          danger
          busy={bulkBusy}
          onConfirm={() => executeBulk(bulkConfirm.action, bulkConfirm.targets)}
          onCancel={() => setBulkConfirm(null)}
        />
      )}
    </div>
  );
}
